import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import BackButton from '../components/common/BackButton';
import PixelSpinner from '../components/common/PixelSpinner';
import scannerService from '../services/scannerService';
import mitreService from '../services/mitreService';
import { generateScanResultsPDF } from '../utils/pdfGenerator';
import AIEnhancedBadge from '../components/vulnerabilities/AIEnhancedBadge';
import RootCauseSection from '../components/vulnerabilities/RootCauseSection';
import SecurityImpactSection from '../components/vulnerabilities/SecurityImpactSection';
import RAGRemediationSection from '../components/vulnerabilities/RAGRemediationSection';
import RAGMetadataPanel from '../components/vulnerabilities/RAGMetadataPanel';
// ConnectionStatus intentionally removed — polling badge not shown on results page
import useScanProgress from '../hooks/useScanProgress';
import { Accordion, ExpandableContent, TabbedInterface } from '../components/disclosure';
import ReactMarkdown from 'react-markdown';
import { featureFlags } from '../config/featureFlags';
// Helper: Build a keyword regex from a technique's name/description
function buildTechniqueRegex(technique) {
  // Use name, description, and platforms for pattern matching
  const keywords = [];
  if (technique.name) keywords.push(technique.name);
  if (technique.description) keywords.push(technique.description);
  if (Array.isArray(technique.platforms)) keywords.push(...technique.platforms);
  // Remove special chars, split, and join for a broad match
  const pattern = keywords.join(' ').replace(/[^\w\s]/g, ' ');
  // Only use words longer than 3 chars
  const words = Array.from(new Set(pattern.split(/\s+/).filter(w => w.length > 3)));
  if (!words.length) return null;
  return new RegExp(words.join('|'), 'i');
}
import './ScanResults.css';

const severityOrder = ['critical', 'high', 'medium', 'low'];
const severityWeights = {
  critical: 10,
  high: 7.5,
  medium: 5,
  low: 2,
  info: 1,
  unknown: 0.5
};

const USD_TO_INR = 83; // Approximate conversion to display rupee amounts

const deriveRiskLevel = (score) => {
  if (score === null || score === undefined) return 'Unknown';
  if (score >= 8) return 'Critical';
  if (score >= 6) return 'High';
  if (score >= 4) return 'Medium';
  if (score >= 2) return 'Low';
  return 'Minimal';
};

// Dynamic duration calculation based on vulnerability count and CVSS
const computeDynamicDuration = (itemCount, avgCvss, baseMultiplier = 1) => {
  if (itemCount === 0) return 0;

  // Base hours per vulnerability by complexity
  const hoursPerVuln = avgCvss >= 7 ? 8 : avgCvss >= 4 ? 4 : 2;
  const totalHours = itemCount * hoursPerVuln * baseMultiplier;

  // Convert to days (6 productive hours per day)
  const days = Math.ceil(totalHours / 6);

  // Add buffer for testing/validation (20%)
  return Math.max(1, Math.ceil(days * 1.2));
};

const computeTimelineDuration = (timelineInfo, fallback, itemCount = 0, avgCvss = 0) => {
  // If we have actual items, calculate dynamically
  if (itemCount > 0) {
    return computeDynamicDuration(itemCount, avgCvss);
  }

  if (!timelineInfo) {
    return fallback;
  }

  if (typeof timelineInfo.duration_days === 'number') {
    return Math.max(1, timelineInfo.duration_days);
  }

  if (typeof timelineInfo.target_days === 'number') {
    return Math.max(1, timelineInfo.target_days);
  }

  if (typeof timelineInfo.window_days === 'number') {
    return Math.max(1, timelineInfo.window_days);
  }

  const estimatedHours = (timelineInfo.items || []).reduce((sum, item) => {
    const hours = item?.estimated_hours || item?.hours || 0;
    return sum + (typeof hours === 'number' ? hours : 0);
  }, 0);

  if (estimatedHours) {
    return Math.max(1, Math.ceil(estimatedHours / 6));
  }

  return fallback;
};

// Dynamic label generation based on actual week numbers
const getDynamicLabel = (weekStart, weekEnd, priority) => {
  const weekStartNum = Math.ceil(weekStart / 7);
  const weekEndNum = Math.ceil(weekEnd / 7);

  if (weekStartNum === weekEndNum || weekEndNum === 0) {
    return `Week ${Math.max(1, weekStartNum)}`;
  }
  return `Week ${Math.max(1, weekStartNum)}-${weekEndNum}`;
};

const pluralize = (count, singular, plural) => {
  if (count === 1) {
    return singular;
  }
  if (plural) {
    return plural;
  }
  return `${singular}s`;
};

const lowSignalSummaryPattern = /(potential financial exposure|remediation investment|roi|potential loss vs investment|compliance impact:\s*owasp_top_10)/i;

const RetroBriefText = ({ text, maxLines = 100 }) => {
  if (!text) return null;
  const lines = String(text).split(/\n+/).filter(line => line.trim()).slice(0, maxLines);
  const renderBold = (str) => {
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="retro-brief-content">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        // Check if heading: ALL CAPS (allowing numbers, spaces, punctuation) and length < 80
        const isHeading = /^[A-Z0-9\s&()\-:,]{5,80}$/.test(trimmed) && trimmed === trimmed.toUpperCase();
        // Check if bullet point
        const isBullet = trimmed.startsWith('•') || trimmed.startsWith('- ');
        
        if (isHeading) {
          return <h4 key={idx} className="retro-brief-heading">{trimmed}</h4>;
        } else if (isBullet) {
          const bulletText = trimmed.replace(/^[\s\-•]+/, '');
          return <div key={idx} className="retro-brief-bullet"><span className="bullet-marker">■</span> {renderBold(bulletText)}</div>;
        } else {
          // If it starts with a number like "1. ", it could also be a top level heading
          const isNumberedHeading = /^\d+\.\s+[A-Z0-9\s&()\-:,]+$/.test(trimmed) && trimmed === trimmed.toUpperCase();
          if (isNumberedHeading) {
            return <h3 key={idx} className="retro-brief-heading-main">{trimmed}</h3>;
          }
          return <p key={idx} className="retro-brief-body">{renderBold(trimmed)}</p>;
        }
      })}
    </div>
  );
};

const prepareSummaryParagraphs = (text, maxParagraphs = 4) => {
  const paragraphs = String(text || '')
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const evidenceBacked = paragraphs.filter((paragraph) => !lowSignalSummaryPattern.test(paragraph));
  return (evidenceBacked.length ? evidenceBacked : paragraphs).slice(0, maxParagraphs);
};

const hasUsefulBusinessImpact = (businessImpact = {}) => {
  return ['reputational_damage', 'legal_implications', 'operational_impact']
    .some((key) => {
      const value = businessImpact?.[key];
      return value && !/^n\/?a$/i.test(String(value).trim());
    });
};

const ScanResults = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary'); // Default to Mission Brief
  const [briefText, setBriefText] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState(null);
  const [briefCached, setBriefCached] = useState(false);
  const [detailedText, setDetailedText] = useState('');
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedError, setDetailedError] = useState(null);
  const [detailedCached, setDetailedCached] = useState(false);
  const [detailedFetchAttempted, setDetailedFetchAttempted] = useState(false);
  const [detailedRetryAttempts, setDetailedRetryAttempts] = useState(0);
  const [detailedRetryInProgress, setDetailedRetryInProgress] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const [globalFilters, setGlobalFilters] = useState({
    environment: 'all',
    assetType: 'all',
    exposure: 'all',
    query: ''
  });

  // MITRE techniques state
  const [mitreTechniques, setMitreTechniques] = useState([]);
  const [mitreLoading, setMitreLoading] = useState(false);
  const [mitreError, setMitreError] = useState(null);
  const [showAllThreats, setShowAllThreats] = useState(false);
  const [activeChain, setActiveChain] = useState('primary');

  const allVulnerabilities = useMemo(() => results?.vulnerabilities || [], [results]);

  const filterOptions = useMemo(() => {
    const environmentSet = new Set();
    const assetTypeSet = new Set();
    const exposureSet = new Set();

    allVulnerabilities.forEach((vuln) => {
      const environment =
        vuln.environment ||
        vuln.asset_environment ||
        vuln.environment_name ||
        vuln.env ||
        'unknown';
      const assetType =
        vuln.asset_type ||
        vuln.asset_category ||
        vuln.asset_kind ||
        (vuln.asset ? 'asset' : 'web') ||
        'unknown';
      let exposure = vuln.exposure_level || vuln.exposure || '';
      if (!exposure) {
        if (vuln.internet_exposed === true) exposure = 'internet';
        if (vuln.internet_exposed === false) exposure = 'internal';
      }
      exposure = exposure || 'unknown';

      environmentSet.add(String(environment).toLowerCase());
      assetTypeSet.add(String(assetType).toLowerCase());
      exposureSet.add(String(exposure).toLowerCase());
    });

    return {
      environments: Array.from(environmentSet).sort(),
      assetTypes: Array.from(assetTypeSet).sort(),
      exposures: Array.from(exposureSet).sort()
    };
  }, [allVulnerabilities]);

  const filteredVulnerabilities = useMemo(() => {
    const query = globalFilters.query.trim().toLowerCase();

    return allVulnerabilities.filter((vuln) => {
      const environment = String(
        vuln.environment ||
        vuln.asset_environment ||
        vuln.environment_name ||
        vuln.env ||
        'unknown'
      ).toLowerCase();
      const assetType = String(
        vuln.asset_type ||
        vuln.asset_category ||
        vuln.asset_kind ||
        (vuln.asset ? 'asset' : 'web') ||
        'unknown'
      ).toLowerCase();
      let exposure = String(vuln.exposure_level || vuln.exposure || '').toLowerCase();
      if (!exposure) {
        if (vuln.internet_exposed === true) exposure = 'internet';
        if (vuln.internet_exposed === false) exposure = 'internal';
      }
      exposure = exposure || 'unknown';

      const searchableBlob = [
        vuln.title,
        vuln.name,
        vuln.cve_id,
        vuln.asset,
        vuln.host,
        vuln.url,
        vuln.endpoint,
        vuln.mitre_technique,
        vuln.mitre_id
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesEnvironment = globalFilters.environment === 'all' || environment === globalFilters.environment;
      const matchesAssetType = globalFilters.assetType === 'all' || assetType === globalFilters.assetType;
      const matchesExposure = globalFilters.exposure === 'all' || exposure === globalFilters.exposure;
      const matchesQuery = !query || searchableBlob.includes(query);

      return matchesEnvironment && matchesAssetType && matchesExposure && matchesQuery;
    });
  }, [allVulnerabilities, globalFilters]);

  const filtersActive = useMemo(() => {
    return globalFilters.environment !== 'all'
      || globalFilters.assetType !== 'all'
      || globalFilters.exposure !== 'all'
      || Boolean(globalFilters.query.trim());
  }, [globalFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (globalFilters.environment !== 'all') count += 1;
    if (globalFilters.assetType !== 'all') count += 1;
    if (globalFilters.exposure !== 'all') count += 1;
    if (globalFilters.query.trim()) count += 1;
    return count;
  }, [globalFilters]);

  // PDF Download Handler
  const handleDownloadPDF = async () => {
    setPdfGenerating(true);
    try {
      console.log('Starting PDF generation...');
      const result = await generateScanResultsPDF(
        scanId,
        results?.target_url,
        results
      );

      if (result.success) {
        console.log('PDF generated successfully:', result.filename);
        // Success - PDF will be auto-downloaded by browser
      } else {
        console.error('PDF generation failed:', result.error);
        alert(`Failed to generate PDF: ${result.error || 'Unknown error'}. Please try again.`);
      }
    } catch (error) {
      console.error('PDF download error:', error);
      alert(`An error occurred while generating the PDF: ${error.message || 'Unknown error'}`);
    } finally {
      setPdfGenerating(false);
    }
  };

  // Fetch MITRE techniques on mount
  useEffect(() => {
    setMitreLoading(true);
    mitreService.getAllTechniques()
      .then(setMitreTechniques)
      .catch((e) => setMitreError(e.message || 'Failed to load MITRE techniques'))
      .finally(() => setMitreLoading(false));
  }, []);

  const vulnerabilityStats = useMemo(() => {
    const counts = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      info: 0,
      unknown: 0
    };

    const grouped = {
      critical: [],
      high: [],
      medium: [],
      low: [],
      info: [],
      unknown: []
    };

    if (!filteredVulnerabilities.length) {
      return {
        total: 0,
        counts,
        grouped,
        avgCvss: null,
        highest: null,
        cvssCount: 0
      };
    }

    let totalCvss = 0;
    let cvssCount = 0;
    let highest = null;

    filteredVulnerabilities.forEach((vuln) => {
      const severity = (vuln.severity || 'unknown').toLowerCase();
      const bucket = grouped[severity] ? severity : 'unknown';
      grouped[bucket].push(vuln);
      counts[bucket] += 1;

      if (typeof vuln.cvss_score === 'number' && !Number.isNaN(vuln.cvss_score)) {
        totalCvss += vuln.cvss_score;
        cvssCount += 1;
        if (!highest || (vuln.cvss_score ?? 0) > (highest.cvss_score ?? 0)) {
          highest = vuln;
        }
      }
    });

    const avgCvss = cvssCount ? totalCvss / cvssCount : null;

    return {
      total: filteredVulnerabilities.length,
      counts,
      grouped,
      avgCvss,
      highest,
      cvssCount
    };
  }, [filteredVulnerabilities]);

  const normalizedRisk = useMemo(() => {
    const baseRisk = results?.risk_assessment || {};
    const fallbackCounts = vulnerabilityStats.counts || {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      unknown: 0
    };

    const vulnerabilityCount = baseRisk.vulnerability_count ?? vulnerabilityStats.total;
    const criticalCount = baseRisk.critical_count ?? fallbackCounts.critical;
    const highCount = baseRisk.high_count ?? fallbackCounts.high;
    const mediumCount = baseRisk.medium_count ?? fallbackCounts.medium;
    const lowCount = baseRisk.low_count ?? fallbackCounts.low;

    // Use provided score only if it's a finite number AND either:
    // 1. It's greater than 0, OR
    // 2. There are no vulnerabilities (score of 0 is correct)
    const providedScore = Number.isFinite(baseRisk.overall_risk_score)
      ? Number(baseRisk.overall_risk_score)
      : null;

    // If backend returned 0 but we have vulnerabilities, recalculate
    const shouldRecalculate = filtersActive || (providedScore === 0 && vulnerabilityStats.total > 0);

    console.log('[RISK] Risk calculation:', {
      providedScore,
      vulnerabilityTotal: vulnerabilityStats.total,
      avgCvss: vulnerabilityStats.avgCvss,
      shouldRecalculate,
      fallbackCounts
    });

    let fallbackScore = 0;
    if (vulnerabilityStats.avgCvss !== null) {
      fallbackScore = vulnerabilityStats.avgCvss;
    } else if (vulnerabilityStats.total) {
      const weightedSum = severityOrder.reduce((sum, severity) => {
        const count = fallbackCounts[severity] || 0;
        return sum + count * (severityWeights[severity] || severityWeights.unknown);
      }, 0);
      fallbackScore = Math.min(10, weightedSum / vulnerabilityStats.total);
    }

    const normalizedScore = (providedScore !== null && !shouldRecalculate)
      ? providedScore
      : Number(fallbackScore.toFixed(2));

    // If we recalculated the score, also recalculate the risk level
    const riskLevel = shouldRecalculate
      ? deriveRiskLevel(normalizedScore)
      : (baseRisk.risk_level || deriveRiskLevel(normalizedScore));

    return {
      ...baseRisk,
      overall_risk_score: normalizedScore,
      risk_level: riskLevel,
      vulnerability_count: vulnerabilityCount,
      critical_count: criticalCount,
      high_count: highCount,
      medium_count: mediumCount,
      low_count: lowCount
    };
  }, [results, vulnerabilityStats, filtersActive]);

  // Centralized shared metrics - used across all tabs for consistency
  const sharedMetrics = useMemo(() => {
    const criticalCount = vulnerabilityStats.counts?.critical || 0;
    const highCount = vulnerabilityStats.counts?.high || 0;
    const mediumCount = vulnerabilityStats.counts?.medium || 0;
    const lowCount = vulnerabilityStats.counts?.low || 0;
    const infoCount = vulnerabilityStats.counts?.info || 0;
    const totalIssues = vulnerabilityStats.total || 0;

    // Cost calculations - Based on industry standards (IBM Cost of Data Breach 2024)
    const baseCriticalCost = 50000;
    const baseHighCost = 25000;
    const baseMediumCost = 12000;
    const baseLowCost = 5000;
    const baseInfoCost = 2000;

    const calculateCost = (vulns, baseCost) => {
      if (!vulns || vulns.length === 0) return 0;
      return vulns.reduce((sum, v) => {
        const cvssMultiplier = v.cvss_score ? Math.max(0.5, v.cvss_score / 5) : 1;
        return sum + (baseCost * cvssMultiplier);
      }, 0);
    };

    const estimatedCostUSD = Math.round(
      calculateCost(vulnerabilityStats.grouped?.critical, baseCriticalCost) +
      calculateCost(vulnerabilityStats.grouped?.high, baseHighCost) +
      calculateCost(vulnerabilityStats.grouped?.medium, baseMediumCost) +
      calculateCost(vulnerabilityStats.grouped?.low, baseLowCost) +
      calculateCost(vulnerabilityStats.grouped?.info, baseInfoCost)
    );

    const potentialLossUSD = Math.round(
      (calculateCost(vulnerabilityStats.grouped?.critical, baseCriticalCost) * 15) +
      (calculateCost(vulnerabilityStats.grouped?.high, baseHighCost) * 10) +
      (calculateCost(vulnerabilityStats.grouped?.medium, baseMediumCost) * 6) +
      (calculateCost(vulnerabilityStats.grouped?.low, baseLowCost) * 3) +
      (calculateCost(vulnerabilityStats.grouped?.info, baseInfoCost) * 1.5)
    );

    const estimatedCost = Math.round(estimatedCostUSD * USD_TO_INR);
    const potentialLoss = Math.round(potentialLossUSD * USD_TO_INR);

    // Effort hours calculation
    const effortHours = Math.round(
      (vulnerabilityStats.grouped?.critical || []).reduce((sum, v) => sum + Math.max(4, (v.cvss_score || 9) * 0.8), 0) +
      (vulnerabilityStats.grouped?.high || []).reduce((sum, v) => sum + Math.max(3, (v.cvss_score || 7) * 0.6), 0) +
      (vulnerabilityStats.grouped?.medium || []).reduce((sum, v) => sum + Math.max(2, (v.cvss_score || 5) * 0.4), 0) +
      (vulnerabilityStats.grouped?.low || []).reduce((sum, v) => sum + Math.max(1, (v.cvss_score || 3) * 0.3), 0) +
      (infoCount * 0.5)
    );

    const netBenefit = potentialLoss - estimatedCost;
    const roiPercentage = estimatedCost > 0 ? Math.round((netBenefit / estimatedCost) * 100) : 0;

    // Risk score calculation - NOTE: This is a different metric from normalizedRisk.overall_risk_score
    // normalizedRisk uses 0-10 CVSS-like scale, while this is a weighted count-based score (0-100)
    // For consistency, we'll use a 0-10 scale to match normalizedRisk
    const weightedScore = (criticalCount * 10 + highCount * 7.5 + mediumCount * 5 + lowCount * 2 + infoCount * 0.5);
    const maxPossibleScore = Math.max(1, totalIssues * 10); // Normalize by max possible
    const riskScore = totalIssues > 0 ? Math.min(10, Number((weightedScore / totalIssues).toFixed(1))) : 0;
    const riskLevel = riskScore >= 8 ? 'Critical' : riskScore >= 6 ? 'High' : riskScore >= 4 ? 'Medium' : riskScore >= 2 ? 'Low' : 'Minimal';

    // Timeline calculations
    const weeksNeeded = Math.max(1, Math.ceil(effortHours / 40));
    const maxWeeks = Math.max(weeksNeeded + 1, Math.ceil(effortHours / 20));
    const estimatedTimeline = totalIssues > 0 ? `${weeksNeeded} - ${maxWeeks} weeks` : 'No remediation needed';

    // Team composition
    const teamComposition = {
      'Security Engineers': criticalCount > 0 ? Math.max(2, Math.ceil(criticalCount / 2)) : Math.max(1, Math.ceil(highCount / 3)),
      'Senior Developers': Math.max(1, Math.ceil((criticalCount + highCount) / 3)),
      'Developers': Math.max(1, Math.ceil((mediumCount + lowCount) / 4)),
      'QA Engineers': Math.max(1, Math.ceil(totalIssues / 10))
    };

    return {
      criticalCount,
      highCount,
      mediumCount,
      lowCount,
      infoCount,
      totalIssues,
      estimatedCost,
      potentialLoss,
      netBenefit,
      roiPercentage,
      effortHours,
      riskScore,
      riskLevel,
      weeksNeeded,
      maxWeeks,
      estimatedTimeline,
      teamComposition,
      budgetRange: totalIssues > 0
        ? `₹${Math.round(estimatedCost * 0.8).toLocaleString('en-IN')} - ₹${Math.round(estimatedCost * 1.2).toLocaleString('en-IN')}`
        : 'N/A'
    };
  }, [vulnerabilityStats]);

  const aiInsightsCount = results?.ai_analysis?.length || 0;
  const tabSections = results?.structured_report?.tab_sections || {};
  const missionBriefSection = tabSections.mission_brief || {};
  const reconReportSection = tabSections.recon_report || {};
  const threatCatalogSection = tabSections.threat_catalog || {};
  const attackMatrixSection = tabSections.attack_matrix || {};
  const defensePlaybookSection = tabSections.defense_playbook || {};
  const intelAnalysisSection = tabSections.intel_analysis || {};

  // Keep these hooks before any early return paths (loading/error/no-results)
  // to preserve stable hook order across renders.
  const missionBriefBullets = useMemo(() => {
    // Helper to format currency and personalize
    const formatText = (text) => {
      if (!text) return '';
      // Convert $ to rupees
      let formatted = text.replace(/\$(\d+(?:,\d+)*(?:\.\d+)?)/g, (match, p1) => {
        const val = parseFloat(p1.replace(/,/g, ''));
        if (isNaN(val)) return match;
        return `₹${(val * USD_TO_INR).toLocaleString('en-IN')}`;
      });
      return formatted;
    };

    let rawText = (briefText || detailedText || '');
    // Split on periods or newlines to make them actual short bullets instead of a wall of text
    const summaryBullets = rawText
      .split(/(?<=\.)\s+|\n+/)
      .map(line => formatText(line.trim()))
      .filter(line => line.length > 10 && !line.match(/^[\d\.\s]+EXECUTIVE MISSION BRIEF$/i))
      .slice(0, 4);

    const topRiskBullets = (missionBriefSection.top_risks || [])
      .slice(0, 2)
      .map((risk) => risk?.title)
      .filter(Boolean);

    const fallback = [
      `${vulnerabilityStats.total} findings identified across current scan scope for ${results?.target_url || 'target'}.`,
      `${vulnerabilityStats.counts.critical || 0} critical and ${vulnerabilityStats.counts.high || 0} high-priority findings require immediate triage.`,
      `Current normalized risk score is ${normalizedRisk.overall_risk_score ?? 0} (${normalizedRisk.risk_level || 'Unknown'}).`
    ];

    let combined = [...summaryBullets];
    if (combined.length === 0) {
        combined = [...topRiskBullets, ...fallback];
    }
    return combined.slice(0, 5);
  }, [briefText, detailedText, missionBriefSection, vulnerabilityStats, normalizedRisk, results?.target_url]);

  const missionBriefKpis = useMemo(() => {
    const exposedAssetsCount = filteredVulnerabilities.filter(
      (v) => v.internet_exposed === true || String(v.exposure_level || '').toLowerCase() === 'internet'
    ).length;
    const patchLatency = intelAnalysisSection?.patch_intelligence?.average_patch_latency_days;

    return [
      { label: 'Risk Score', value: normalizedRisk.overall_risk_score ?? 0, tone: 'cyan' },
      { label: 'Total Findings', value: vulnerabilityStats.total, tone: 'yellow' },
      { label: 'Critical + High', value: (vulnerabilityStats.counts.critical || 0) + (vulnerabilityStats.counts.high || 0), tone: 'coral' },
      { label: 'Patch Age', value: patchLatency !== undefined ? `${patchLatency}d` : 'N/A', tone: 'green' },
      { label: 'Internet Exposed', value: exposedAssetsCount, tone: 'pink' }
    ].slice(0, 4);
  }, [filteredVulnerabilities, normalizedRisk, vulnerabilityStats, intelAnalysisSection]);

  // Calculate MITRE count - use backend data if available, otherwise estimate from vulnerabilities
  const mitreCount = useMemo(() => {
    // First check if backend provided MITRE mappings
    if (results?.mitre_mapping?.length > 0) {
      return results.mitre_mapping.length;
    }

    // If no backend mappings but we have vulnerabilities, estimate potential mappings
    // This ensures the Attack Matrix tab shows up when there are vulnerabilities to map
    if (filteredVulnerabilities.length > 0) {
      // Count unique potential MITRE mappings based on vulnerability patterns
      const patterns = [
        /sql injection|sqli/i, /xss|cross-site scripting/i, /command injection|rce/i,
        /authentication|login|password/i, /file upload/i, /path traversal|lfi/i,
        /ssrf/i, /xxe/i, /csrf/i, /\.git|\.svn/i, /exposed|sensitive|leaked/i,
        /header|cors|csp/i, /certificate|ssl|tls/i, /cookie|session/i,
        /admin|management/i, /outdated|cve/i, /information disclosure/i
      ];

      const seenPatterns = new Set();
      for (const vuln of filteredVulnerabilities) {
        const text = `${vuln.title || ''} ${vuln.description || ''}`;
        for (let i = 0; i < patterns.length; i++) {
          if (patterns[i].test(text)) {
            seenPatterns.add(i);
          }
        }
      }
      return seenPatterns.size;
    }

    return 0;
  }, [results, filteredVulnerabilities]);

  const remediationRecommendationsCount = results?.remediation_strategies?.recommendations?.length || 0;
  const summaryTabVisible = Boolean(
    briefLoading ||
    briefError ||
    briefText ||
    detailedLoading ||
    detailedError ||
    detailedText
  );

  const overviewCards = useMemo(() => {
    if (!results) {
      return [];
    }

    const total = vulnerabilityStats.total;
    const critical = normalizedRisk.critical_count || 0;
    const high = normalizedRisk.high_count || 0;
    const medium = normalizedRisk.medium_count || 0;
    const low = normalizedRisk.low_count || 0;
    const info = vulnerabilityStats.counts?.info || 0;
    const mediumLowTotal = medium + low;
    const avgCvss = vulnerabilityStats.avgCvss;
    const topCritical = vulnerabilityStats.grouped?.critical?.[0]?.title;
    const topHigh = vulnerabilityStats.grouped?.high?.[0]?.title;
    const topInfo = vulnerabilityStats.grouped?.info?.[0]?.title;
    const topFinding = vulnerabilityStats.highest?.title || vulnerabilityStats.highest?.name;

    // Threat Intelligence data
    const threatIntel = results.threat_intel || {};
    const reputation = threatIntel.reputation || {};
    const vt = threatIntel.virustotal || {};
    const gsb = threatIntel.google_safe_browsing || {};
    const abuse = threatIntel.abuseipdb || {};
    const shodan = threatIntel.shodan || {};
    const vulners = threatIntel.vulners || {};
    const riskIndicators = threatIntel.risk_indicators || [];

    // Build threat intel description
    const buildThreatIntelDesc = () => {
      const parts = [];

      if (reputation.score !== undefined) {
        const scoreLabel = reputation.score >= 70 ? 'Good' : reputation.score >= 40 ? 'Moderate' : 'Poor';
        parts.push(`Reputation: ${reputation.score}/100 (${scoreLabel})`);
      }

      if (vt.malicious > 0 || vt.suspicious > 0) {
        parts.push(`VirusTotal: ${vt.malicious || 0} malicious, ${vt.suspicious || 0} suspicious`);
      } else if (vt.harmless > 0) {
        parts.push(`VirusTotal: Clean (${vt.harmless} vendors)`);
      }

      if (gsb.is_flagged) {
        parts.push(`Google Safe Browsing: FLAGGED`);
      }

      if (abuse.abuse_confidence_score > 0) {
        parts.push(`AbuseIPDB: ${abuse.abuse_confidence_score}% confidence`);
      }

      if (shodan.vuln_count > 0) {
        parts.push(`Shodan: ${shodan.vuln_count} known vulns`);
      }

      // Add Vulners exploit info
      if (vulners.total_exploits > 0) {
        parts.push(`Vulners: ${vulners.total_exploits} exploits found`);
      }

      if (parts.length === 0) {
        return threatIntel.data_sources_queried
          ? `${threatIntel.data_sources_queried} intel sources queried — no active threats detected.`
          : 'Threat intelligence not yet collected.';
      }

      return parts.slice(0, 2).join(' • ') + (parts.length > 2 ? ` (+${parts.length - 2} more)` : '');
    };

    return [
      {
        key: 'critical',
        number: '01',
        title: 'Critical Issues',
        color: 'cyan',
        description: critical
          ? `${pluralize(critical, 'critical vulnerability')} detected${topCritical ? ` — start with ${topCritical}.` : '.'}`
          : 'No critical vulnerabilities detected in this scan.'
      },
      {
        key: 'high',
        number: '02',
        title: 'High Priority',
        color: 'coral',
        description: high
          ? `${pluralize(high, 'high-severity issue')} queued for the next remediation window${topHigh ? ` (focus on ${topHigh})` : ''}.`
          : 'No high-severity issues were reported.'
      },
      {
        key: 'mediumLow',
        number: '03',
        title: 'Medium & Low Risk',
        color: 'green',
        description: mediumLowTotal
          ? `${pluralize(mediumLowTotal, 'lower-severity finding')} scheduled for maintenance cycles.`
          : 'Medium and low risk surfaces are currently clear.'
      },
      {
        key: 'threat_intel',
        number: '04',
        title: 'Threat Intelligence',
        color: 'purple',
        description: buildThreatIntelDesc()
      },
      {
        key: 'coverage',
        number: '05',
        title: 'Coverage & Mapping',
        color: 'pink',
        description: mitreCount
          ? `${pluralize(mitreCount, 'MITRE technique')} mapped to help trace attack paths.`
          : 'No MITRE techniques mapped for this scan run.'
      },
      {
        key: 'insights',
        number: '06',
        title: 'AI Insights',
        color: 'yellow',
        description: aiInsightsCount
          ? `${aiInsightsCount} curated insight${aiInsightsCount === 1 ? '' : 's'} ready to guide remediation.`
          : 'AI insights will populate as scans collect additional context.'
      }
    ];
  }, [results, normalizedRisk, vulnerabilityStats, mitreCount, aiInsightsCount]);

  const hasRemediationData = useMemo(() => {
    // Show Defense Playbook tab if there are vulnerabilities OR remediation strategies
    // The component will generate default strategies from vulnerabilities if needed
    if (vulnerabilityStats.total > 0) {
      return true;
    }

    // Also check normalizedRisk counts from risk_assessment
    const riskTotal = (normalizedRisk.critical_count || 0) +
      (normalizedRisk.high_count || 0) +
      (normalizedRisk.medium_count || 0) +
      (normalizedRisk.low_count || 0);
    if (riskTotal > 0) {
      return true;
    }

    const strategies = results?.remediation_strategies;
    if (!strategies) {
      return false;
    }

    const {
      recommendations,
      priority_matrix: priorityMatrix,
      timeline,
      cost_benefit: costBenefit,
      resource_allocation: resourceAllocation
    } = strategies;

    return Boolean(
      (recommendations && recommendations.length) ||
      (priorityMatrix && Object.keys(priorityMatrix).length) ||
      (timeline && Object.keys(timeline).length) ||
      (costBenefit && Object.keys(costBenefit).length) ||
      (resourceAllocation && Object.keys(resourceAllocation).length)
    );
  }, [results, vulnerabilityStats.total, normalizedRisk]);

  const availableTabs = useMemo(() => {
    if (!results) {
      return [];
    }

    return [
      {
        id: 'summary',
        label: 'Mission Brief',
        display: 'Mission Brief',
        visible: true  // Always show - will display loading/error/empty state as needed
      },
      {
        id: 'overview',
        label: 'Recon Report',
        display: 'Recon Report'
      },
      {
        id: 'vulnerabilities',
        label: 'Threat Catalog',
        display: vulnerabilityStats.total ? `Threat Catalog (${vulnerabilityStats.total})` : 'Threat Catalog'
      },
      {
        id: 'mitre',
        label: 'Attack Matrix',
        display: mitreCount ? `Attack Matrix (${mitreCount})` : 'Attack Matrix',
        visible: true  // Always show - will generate client-side mappings or show empty state
      },
      {
        id: 'remediation',
        label: 'Defense Playbook',
        display: hasRemediationData
          ? `Defense Playbook (${vulnerabilityStats.total || remediationRecommendationsCount || 0})`
          : 'Defense Playbook',
        visible: true  // Always show - will display empty state if no data
      },
      {
        id: 'ai',
        label: 'Intel Analysis',
        display: aiInsightsCount ? `Intel Analysis (${aiInsightsCount})` : 'Intel Analysis',
        visible: true  // Always show - will display empty state or generate from vulns
      }
    ].filter((tab) => tab.visible === undefined ? true : tab.visible);
  }, [results, vulnerabilityStats.total, mitreCount, hasRemediationData, aiInsightsCount, summaryTabVisible]);

  useEffect(() => {
    if (!results || !availableTabs.length) {
      return;
    }

    if (!availableTabs.some((tab) => tab.id === activeTab)) {
      const missionDefault = availableTabs.find((tab) => tab.id === 'summary')?.id || availableTabs[0].id;
      setActiveTab(missionDefault);
    }
  }, [results, availableTabs, activeTab]);

  useEffect(() => {
    const handleTabShortcuts = (event) => {
      const isTypingContext = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)
        || event.target?.isContentEditable;
      if (isTypingContext) {
        return;
      }

      const shortcutIndex = Number(event.key) - 1;
      if (shortcutIndex < 0 || shortcutIndex > 5) {
        return;
      }

      const shortcutTab = availableTabs[shortcutIndex];
      if (!shortcutTab) {
        return;
      }

      event.preventDefault();
      setActiveTab(shortcutTab.id);
    };

    window.addEventListener('keydown', handleTabShortcuts);
    return () => window.removeEventListener('keydown', handleTabShortcuts);
  }, [availableTabs]);

  const fetchResults = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) {
        setLoading(true);
      }
      console.log('[SCAN] Fetching scan results for ID:', scanId);
      const data = await scannerService.getScanResults(scanId);
      console.log('[DATA] Received scan results:', data);
      console.log('[DATA] Vulnerabilities count:', data?.vulnerabilities?.length || 0);

      // Log severity distribution from raw API data
      if (data?.vulnerabilities?.length > 0) {
        const severityBreakdown = data.vulnerabilities.reduce((acc, v) => {
          const sev = (v.severity || 'missing').toLowerCase();
          acc[sev] = (acc[sev] || 0) + 1;
          return acc;
        }, {});
        console.log('[DATA] Severity breakdown from API:', severityBreakdown);
        console.log('[DATA] First 3 vulnerabilities sample:', data.vulnerabilities.slice(0, 3).map(v => ({
          title: v.title || v.name,
          severity: v.severity,
          cvss_score: v.cvss_score,
          scanner_source: v.scanner_source
        })));
      }

      console.log('[DATA] Risk assessment:', data?.risk_assessment);
      console.log('[DATA] MITRE mapping count:', data?.mitre_mapping?.length || 0);
      console.log('[DATA] AI analysis count:', data?.ai_analysis?.length || 0);

      // Ensure data structure is correct
      const normalizedData = {
        ...data,
        vulnerabilities: data?.vulnerabilities || [],
        risk_assessment: data?.risk_assessment || {
          overall_risk_score: 0,
          risk_level: 'Unknown',
          vulnerability_count: 0,
          critical_count: 0,
          high_count: 0,
          medium_count: 0,
          low_count: 0
        },
        mitre_mapping: data?.mitre_mapping || [],
        ai_analysis: data?.ai_analysis || [],
        brief_summary: data?.brief_summary || data?.executive_summary || null,
        detailed_summary: data?.detailed_summary || null,
        executive_summary: data?.brief_summary || data?.executive_summary || null
      };

      console.log('[SUCCESS] Normalized data:', normalizedData);
      setResults(normalizedData);
      setError(null);

      // Initialize brief and detailed summaries from distinct backend fields.
      const summary = normalizedData.brief_summary;
      const detailedSummary = normalizedData.detailed_summary;
      const scanStatus = data?.status || 'unknown';
      const scanStage = data?.current_stage || 'unknown';

      if (detailedSummary && detailedSummary.trim().length > 0) {
        setDetailedText(detailedSummary);
        setDetailedCached(true);
        setDetailedError(null);
        setDetailedFetchAttempted(true);
      } else {
        setDetailedText('');
        setDetailedCached(false);
        setDetailedError(null);
        setDetailedFetchAttempted(false);
      }

      if (summary && summary.trim().length > 0) {
        console.log('[SUMMARY] Brief summary present:', summary.length, 'characters');
        setBriefText(summary);
        setBriefCached(true);
        setBriefLoading(false);
        setBriefError(null);
      } else if (scanStatus === 'processing' || scanStage !== 'completed') {
        console.log('[SUMMARY] Scan still processing, summary not yet available');
        setBriefText('');
        setBriefCached(false);
        setBriefLoading(true);
        setBriefError(null);
      } else {
        console.warn('[SUMMARY] Scan complete but brief_summary is empty');
        setBriefText('');
        setBriefCached(false);
        setBriefLoading(false);
        setBriefError(null);
      }
    } catch (error) {
      console.error('[ERROR] Error fetching scan results:', error);
      if (!quiet) {
        setError(error.message);
      }
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, [scanId]);

  const liveProgress = useScanProgress(scanId, {
    enabled: Boolean(scanId),
    onComplete: () => fetchResults({ quiet: true }),
  });

  useEffect(() => {
    const liveFindings = liveProgress.vulnerabilities || [];
    const urgentFinding = liveFindings.find((finding) =>
      ['critical', 'high'].includes(String(finding.severity || '').toLowerCase())
    );
    if (urgentFinding) {
      toast.error(`${String(urgentFinding.severity).toUpperCase()}: ${urgentFinding.title || 'Vulnerability found'}`);
    }
  }, [liveProgress.vulnerabilities]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Fetch brief summary when results first load
  useEffect(() => {
    if (!results || (briefText && briefCached)) {
      return;
    }

    const hasFindings = Boolean(
      (results.vulnerabilities && results.vulnerabilities.length > 0) ||
      (results.ai_analysis && results.ai_analysis.length > 0) ||
      (results.risk_assessment && results.risk_assessment.overall_risk_score > 0)
    );

    if (!hasFindings) {
      return;
    }

    let cancelled = false;

    const fetchBriefSummary = async () => {
      try {
        setBriefLoading(true);
        setBriefError(null);
        setBriefCached(false);

        // Fetch brief summary with short character limit
        const response = await scannerService.getScanSummary(scanId, { mode: 'brief', maxChars: 400 });
        if (cancelled) {
          return;
        }

        setBriefText(response?.brief_summary || response?.summary || '');
        setBriefCached(Boolean(response?.cached));
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to fetch brief summary:', err);
          // Don't show error for brief, just use the initial executive_summary if available
          setBriefError(null);
        }
      } finally {
        if (!cancelled) {
          setBriefLoading(false);
        }
      }
    };

    fetchBriefSummary();

    return () => {
      cancelled = true;
    };
  }, [results, scanId]);

  // Fetch detailed summary when Mission Brief tab becomes active
  useEffect(() => {
    if (activeTab !== 'summary' || !results || detailedFetchAttempted) {
      return;
    }

    const hasFindings = Boolean(
      (results.vulnerabilities && results.vulnerabilities.length > 0) ||
      (results.ai_analysis && results.ai_analysis.length > 0) ||
      (results.risk_assessment && results.risk_assessment.overall_risk_score > 0)
    );

    if (!hasFindings) {
      return;
    }

    let cancelled = false;

    const fetchDetailedSummary = async () => {
      try {
        setDetailedLoading(true);
        setDetailedError(null);
        setDetailedCached(false);
        setDetailedFetchAttempted(true);

        // Fetch detailed summary from backend
        const response = await scannerService.getScanSummary(scanId, { mode: 'detailed', force_refresh: false });
        if (cancelled) {
          return;
        }

        setDetailedText(response?.detailed_summary || response?.summary || '');
        setDetailedCached(Boolean(response?.cached));
      } catch (err) {
        if (!cancelled) {
          setDetailedError(err?.message || 'Unable to generate detailed summary');
          setDetailedText('');
        }
      } finally {
        if (!cancelled) {
          setDetailedLoading(false);
        }
      }
    };

    fetchDetailedSummary();

    return () => {
      cancelled = true;
    };
  }, [results, scanId, activeTab]);

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <PixelSpinner size={80} />
          <p style={{ marginTop: '1.5rem', fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-xl)' }}>Loading scan results...</p>
        </div>
      </Layout>
    );
  }

  const currentStatus = String(liveProgress?.status || results?.status || '').toLowerCase();
  const isRunning = Boolean(currentStatus && !['completed', 'failed', 'cancelled'].includes(currentStatus));

  if (isRunning) {
    return (
      <Layout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '2rem' }}>
          <PixelSpinner size={120} />
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: 'var(--font-size-3xl)', fontFamily: 'var(--font-heading)', margin: '0 0 1rem 0' }}>Scan in Progress</h2>
            <div style={{ 
              fontSize: 'var(--font-size-2xl)', 
              fontFamily: 'var(--font-heading)', 
              color: 'var(--scan-text)',
              background: 'var(--color-block-blue)',
              border: '2px solid var(--scan-text)',
              padding: '0.5rem 1rem',
              display: 'inline-block',
              boxShadow: '4px 4px 0 rgba(18,20,23,0.2)'
            }}>
              {liveProgress.progress || 0}%
            </div>
            <p style={{ marginTop: '1.5rem', fontSize: 'var(--font-size-lg)', fontWeight: '500' }}>
              {liveProgress.current_stage || 'Analyzing target...'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-container">
          <h2>Error Loading Results</h2>
          <p>{error}</p>
          <p className="error-scan-id">Scan ID: {scanId}</p>
          <div className="error-actions">
            <button onClick={() => window.location.reload()}>Retry</button>
            <BackButton to="/" label="RETURN TO BASE" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!results) {
    return (
      <Layout>
        <div className="error-container">
          <h2>No Results Found</h2>
          <p>The scan results could not be loaded. The scan may still be in progress.</p>
          <p className="error-scan-id">Scan ID: {scanId}</p>
          <div className="error-actions">
            <button onClick={() => window.location.reload()}>Refresh</button>
            <BackButton to="/" label="RETURN TO BASE" />
          </div>
        </div>
      </Layout>
    );
  }

  const handleLoadDetailedSummary = async () => {
    if (detailedLoading || detailedRetryInProgress) return;

    let cancelled = false;

    try {
      setDetailedLoading(true);
      setDetailedRetryInProgress(true);
      setDetailedError(null);
      setDetailedCached(false);
      setDetailedRetryAttempts(prev => prev + 1);

      // Request detailed summary with force_refresh=true
      const response = await scannerService.getScanSummary(scanId, { 
        mode: 'detailed', 
        force_refresh: true,
        attempt: detailedRetryAttempts + 1
      });
      if (cancelled) {
        return;
      }

      setDetailedText(response?.detailed_summary || response?.summary || '');
      setDetailedCached(Boolean(response?.cached));
      setDetailedError(null);
      setDetailedRetryAttempts(0); // Reset on success
      
      console.log(`[SUMMARY_FETCH] Successfully loaded detailed summary on attempt ${detailedRetryAttempts + 1}`);
    } catch (err) {
      if (!cancelled) {
        const errorMsg = err?.message || 'Unable to generate detailed summary';
        const errorDetail = err?.response?.data?.detail || errorMsg;
        
        // Parse error for better UI messaging
        let displayError = errorMsg;
        let errorStage = 'unknown';
        
        if (typeof errorDetail === 'object' && errorDetail.error) {
          displayError = errorDetail.error;
          errorStage = errorDetail.error_stage || 'enrichment_service';
        }
        
        console.error(`[SUMMARY_ERROR] Attempt ${detailedRetryAttempts + 1} failed:`, displayError, errorStage);
        
        setDetailedError(displayError);
        setDetailedText('');
      }
    } finally {
      if (!cancelled) {
        setDetailedLoading(false);
        setDetailedRetryInProgress(false);
      }
    }
  };

  const handleSummaryRetry = () => {
    handleLoadDetailedSummary();
  };

  const handleTechniquePivot = (technique) => {
    setActiveTab('vulnerabilities');
    if (!technique) {
      return;
    }
    setGlobalFilters((prev) => ({
      ...prev,
      query: technique.id || technique.name || ''
    }));
  };

  const renderVulnerabilitySection = () => {
    const catalogItems = Array.isArray(threatCatalogSection.items) ? threatCatalogSection.items : [];
    let vulns = filteredVulnerabilities.length
      ? filteredVulnerabilities
      : catalogItems;
    console.log('[RENDER] Rendering vulnerability section with', vulns.length, 'vulnerabilities');

    // Log severity distribution at render time
    if (vulns.length > 0) {
      const renderSeverityBreakdown = vulns.reduce((acc, v) => {
        const sev = (v.severity || 'missing').toLowerCase();
        acc[sev] = (acc[sev] || 0) + 1;
        return acc;
      }, {});
      console.log('[RENDER] Severity breakdown at render:', renderSeverityBreakdown);
      console.log('[RENDER] vulnerabilityStats counts:', vulnerabilityStats.counts);
    }

    // Sort vulnerabilities by severity (AI normalized simulation)
    const severityMap = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
    vulns = [...vulns].sort((a, b) => {
      const scoreA = severityMap[(a.severity || 'info').toLowerCase()] || 0;
      const scoreB = severityMap[(b.severity || 'info').toLowerCase()] || 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      // Secondary sort by cvss score if available
      const cvssA = a.cvss_score || a.cvss || 0;
      const cvssB = b.cvss_score || b.cvss || 0;
      return cvssB - cvssA;
    });

    const displayVulns = showAllThreats ? vulns : vulns.slice(0, 10);
    const hasMoreVulns = vulns.length > 10;

    // Random color rotation for visual variety within the flat list
    const colorMap = ['cyan', 'coral', 'green', 'pink', 'yellow'];

    return (
      <section className="results-section detailed-outline">
        <div className="section-header-box coral">
          <h2>Threat Catalog</h2>
          <div className="count-badge">{vulnerabilityStats.total}</div>
        </div>
        {catalogItems.length > 0 && (
          <div className="overview-grid scan-tab-brief-grid">
            <div className="overview-card cyan-bg">
              <div className="card-number">01</div>
              <h3 className="card-title">Catalog Entries</h3>
              <p className="card-content">{catalogItems.length} evidence-backed findings with CVE/asset context.</p>
            </div>
            <div className="overview-card pink-bg">
              <div className="card-number">02</div>
              <h3 className="card-title">Forensic Ready</h3>
              <p className="card-content">Each finding includes who/what/when/where/why plus technical evidence fields.</p>
            </div>
          </div>
        )}
        {vulns.length > 0 ? (
          <div className="flat-threat-list" style={{ marginTop: '2rem' }}>
            <div className="timeline-grid">
              {displayVulns.map((vuln, index) => {
                const colorKey = colorMap[index % colorMap.length];
                return (
                  <div key={vuln.id || vuln.vulnerability_id || index} className={`timeline-item ${colorKey}-bg`}>
                    <div className="timeline-marker">
                      <div className="marker-label">{index + 1}</div>
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-time">
                        <span
                          className={`severity-badge ${vuln.severity ? vuln.severity.toLowerCase() : 'info'}`}
                        >
                          {vuln.severity || 'Info'}
                        </span>
                        {vuln.category && (
                          <span className="category-badge">{vuln.category}</span>
                        )}
                      </div>
                      <h3 className="timeline-title">{vuln.title || vuln.name || 'Security Finding'}</h3>
                      <p className="timeline-desc">{vuln.description || 'No description available'}</p>

                      {/* AI Enhanced Badge */}
                      <AIEnhancedBadge vulnerability={vuln} />

                      {/* RAG-powered analysis sections */}
                      <RootCauseSection
                        vulnerability={vuln}
                        scanId={results?.scan_id || results?.id}
                        isHistorical={results?.status === 'completed'}
                      />
                      <SecurityImpactSection
                        vulnerability={vuln}
                        scanId={results?.scan_id || results?.id}
                        isHistorical={results?.status === 'completed'}
                      />
                      <RAGRemediationSection
                        vulnerability={vuln}
                        scanId={results?.scan_id || results?.id}
                        isHistorical={results?.status === 'completed'}
                      />
                      
                      {/* RAG Metadata Panel (shows AI confidence, sources, etc.) */}
                      {vuln.rag_analysis && <RAGMetadataPanel ragAnalysis={vuln.rag_analysis} />}

                      {vuln.evidence && (
                        <div className="timeline-evidence">
                          <strong>Evidence:</strong>
                          <pre>{JSON.stringify(vuln.evidence, null, 2)}</pre>
                        </div>
                      )}
                      {(vuln.cve_id || (vuln.vulners_exploits && vuln.vulners_exploits.length > 0)) && (
                        <div className="timeline-meta">
                          {vuln.cve_id && <span><strong>CVE:</strong> {vuln.cve_id}</span>}
                          {vuln.vulners_exploits?.length > 0 && <span className="exploit-text">Known exploit available</span>}
                          {vuln.vulners_exploits?.length > 0 && (
                            <span className="exploit-count">({vuln.vulners_exploits.length} found)</span>
                          )}
                        </div>
                      )}
                      {vuln.cvss_score > 0 && (
                        <div className="timeline-meta">
                          <strong>CVSS:</strong> {vuln.cvss_score}
                        </div>
                      )}
                      {(vuln.location || vuln.url) && (
                        <div className="timeline-meta">
                          <strong>Location:</strong> {vuln.location || vuln.url}
                        </div>
                      )}
                      {(vuln.cwe_id || vuln.owasp_category) && (
                        <div className="timeline-meta">
                          {vuln.cwe_id && <span><strong>CWE:</strong> {vuln.cwe_id}</span>}
                          {vuln.owasp_category && <span><strong>OWASP:</strong> {vuln.owasp_category}</span>}
                        </div>
                      )}
                      {vuln.recommendation && (
                        <div className="timeline-recommendation">
                          <strong>Fix:</strong> {vuln.recommendation}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {hasMoreVulns && !showAllThreats && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <button 
                  onClick={() => setShowAllThreats(true)}
                  className="load-all-button"
                >
                  Load All {vulns.length} Findings
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state-card catalog-empty">
            <div className="empty-state-header">
              <div className="empty-state-box">CATALOG</div>
            </div>
            <div className="empty-state-content">
              <h3>No Threats Detected</h3>
              <p>Run a new scan with vulnerability detection to populate the threat catalog.</p>
              <div className="empty-state-suggestions">
                <span className="suggestion-label">Suggestions:</span>
                <ul>
                  <li>Execute a Deep or Standard scan mode for comprehensive coverage</li>
                  <li>Ensure target website is accessible and responsive</li>
                  <li>Verify all scanner modules are enabled in scan settings</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderMITRESection = () => {
    const attackChainData = results?.attack_chain_data;
    const isReady = !!attackChainData;

    if (!isReady) {
      return (
        <section className="results-section mitre-section">
          <div className="section-header-box green">
            <h2>Attack Matrix</h2>
            <div className="count-badge">...</div>
          </div>
          <div className="attack-path-visualization-box detailed-outline" style={{ border: '3px solid var(--scan-text)', background: 'var(--scan-panel)', padding: '1.15rem', marginBottom: '1.5rem', boxShadow: 'var(--scan-shadow-sm)' }}>
            <div className="attack-path-visualization-header" style={{ borderBottom: '3px solid var(--scan-text)', paddingBottom: '0.65rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-3xl)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Tactical Retro Attack Path</h3>
              <p style={{ margin: '0.2rem 0 0', fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-sm)', color: 'var(--scan-muted-text)' }}>Generating threat execution chain...</p>
            </div>
            <div className="svg-responsive-wrapper skeleton-loader" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--scan-card)', border: '3px solid var(--scan-text)' }}>
               <div style={{ color: 'var(--scan-text)', fontFamily: 'var(--font-heading)', letterSpacing: '0.1em', animation: 'pulse 1.5s infinite' }}>[ CONSTRUCTING AI ATTACK CHAIN... ]</div>
            </div>
          </div>
        </section>
      );
    }

    const tacticColors = {
      "Initial Access":        "#4FC3F7",
      "Execution":             "#FFD54F",
      "Persistence":           "#FFD54F",
      "Privilege Escalation":  "#FF8A65",
      "Defense Evasion":       "#CE93D8",
      "Credential Access":     "#FF8A65",
      "Discovery":             "#A5D6A7",
      "Lateral Movement":      "#FF8A65",
      "Collection":            "#FFD54F",
      "Exfiltration":          "#EF9A9A",
      "Impact":                "#EF5350",
    };

    const activeChainNodes = activeChain === 'alternate' && attackChainData.alternate_chain 
      ? attackChainData.alternate_chain 
      : attackChainData.primary_chain || [];

    // Tactic phases to determine columns
    const phases = [...new Set(activeChainNodes.map(n => n.tactic_phase || 1))].sort((a, b) => a - b);
    const FIXED_COLS = 2; // Entry Vectors + Impact
    const mitreColCount = phases.length || 1;
    const totalCols = FIXED_COLS + mitreColCount;

    const COL_WIDTH = 250;
    const COL_GAP = 60;
    const svgWidth = totalCols * COL_WIDTH + (totalCols - 1) * COL_GAP;
    
    // Group nodes by column
    const layoutColumns = []; // Array of arrays: [col0Items, col1Items, ..., colNItems]
    
    // Col 0: Entry Vectors
    const col0Items = (attackChainData.entry_vectors || []).map((ev, i) => ({
      id: `ev-${i}`,
      label: (ev.type || 'ENTRY').toUpperCase(),
      val: ev.value,
      color: tacticColors["Initial Access"],
      isDashed: false
    }));
    if (!col0Items.length) {
      col0Items.push({ id: 'ev-0', label: 'ENTRY PROTOCOL', val: 'UNKNOWN', color: tacticColors["Initial Access"] });
    }
    layoutColumns.push(col0Items);

    // Col 1..N: MITRE Techniques (grouped by phase)
    phases.forEach(phaseNum => {
      const phaseNodes = activeChainNodes.filter(n => (n.tactic_phase || 1) === phaseNum);
      const items = phaseNodes.map(n => {
        // Determine fill color by severity of source vulnerabilities, fallback to tactic color
        let fillIntensity = tacticColors[n.tactic] || '#CE93D8';
        // In real execution, we'd lookup vulns by source_vulnerability_ids and find max severity.
        return {
          id: n.id,
          label: n.id,
          val: (n.short_name || n.name).substring(0, 24),
          fullObj: n,
          color: fillIntensity,
          isDashed: n.detection_difficulty === 'high',
          tooltip: n.attacker_objective
        };
      });
      layoutColumns.push(items);
    });

    // Col N+1: Business Impact
    const finalImpactItems = (attackChainData.business_impact_chain || []).map((imp, i) => ({
      id: `imp-${i}`,
      label: (imp.operational_label || 'IMPACT').toUpperCase(),
      val: new Intl.NumberFormat('en-IN', { style: 'currency', currency: imp.loss_currency || 'USD' }).format(imp.estimated_loss_max || 0),
      color: tacticColors["Impact"],
      isDashed: false
    }));
    if (!finalImpactItems.length) {
      finalImpactItems.push({ id: 'imp-0', label: 'OPERATIONAL IMPACT', val: 'UNKNOWN', color: tacticColors["Impact"] });
    }
    layoutColumns.push(finalImpactItems);

    // Layout configuration
    const nodeW = 210;
    const nodeH = 64;
    const startY = 100;
    const rowSpacing = 100;
    const maxNodesInCol = Math.max(...layoutColumns.map(col => col.length));
    const svgHeight = Math.max(300, startY + (maxNodesInCol - 1) * rowSpacing + 100);

    const columnsX = Array.from({length: totalCols}, (_, i) => (COL_WIDTH / 2) + i * (COL_WIDTH + COL_GAP));
    
    // Position nodes
    const pathNodes = layoutColumns.map((items, colIdx) => {
      const cx = columnsX[colIdx];
      return items.map((item, rowIdx) => {
        const cy = startY + rowIdx * rowSpacing;
        return {
          ...item,
          x: cx - nodeW / 2,
          y: cy - nodeH / 2,
          cx,
          cy
        };
      });
    });

    // Compute Connections
    const connections = [];
    
    // Connect Col0 to first MITRE col
    if (pathNodes[0].length && pathNodes[1] && pathNodes[1].length) {
      pathNodes[0].forEach(n0 => {
        // Connect to entry nodes (chained_from === null)
        const targets = pathNodes[1].filter(n => n.fullObj && !n.fullObj.chained_from);
        if (targets.length) {
          targets.forEach(t => connections.push({ id: `${n0.id}-to-${t.id}`, from: n0, to: t, confidence: 1, dwell: null }));
        } else {
          connections.push({ id: `${n0.id}-to-${pathNodes[1][0].id}`, from: n0, to: pathNodes[1][0], confidence: 1, dwell: null });
        }
      });
    }

    // Connect MITRE nodes via chained_to
    const allMitreNodes = pathNodes.slice(1, -1).flat();
    allMitreNodes.forEach(node => {
      if (node.fullObj && node.fullObj.chained_to) {
        const target = allMitreNodes.find(n => n.id === node.fullObj.chained_to);
        if (target) {
          connections.push({
            id: `${node.id}-to-${target.id}`,
            from: node,
            to: target,
            confidence: node.fullObj.confidence || 0.6,
            dwell: node.fullObj.dwell_time_estimate
          });
        }
      }
    });

    // Connect terminal MITRE nodes to Col N+1 (Impact)
    const terminalNodes = allMitreNodes.filter(n => !n.fullObj || !n.fullObj.chained_to);
    if (terminalNodes.length && pathNodes[totalCols - 1].length) {
      terminalNodes.forEach(tn => {
        pathNodes[totalCols - 1].forEach(imp => {
          connections.push({ id: `${tn.id}-to-${imp.id}`, from: tn, to: imp, confidence: 1, dwell: null });
        });
      });
    }

    // Fallback: single node chain handler (if no connectors were drawn for MITRE)
    if (allMitreNodes.length === 1 && connections.length === 0 && pathNodes[0].length && pathNodes[totalCols - 1].length) {
      // Connect entry to the only node, and only node to impact
      pathNodes[0].forEach(n0 => connections.push({ id: `${n0.id}-to-${allMitreNodes[0].id}`, from: n0, to: allMitreNodes[0], confidence: 1 }));
      pathNodes[totalCols - 1].forEach(imp => connections.push({ id: `${allMitreNodes[0].id}-to-${imp.id}`, from: allMitreNodes[0], to: imp, confidence: 1 }));
    }

    const colHeaders = [
      "ENTRY VECTORS", 
      ...phases.map(p => `PHASE ${p} MITRE`), 
      "BUSINESS IMPACT"
    ];

    return (
      <section className="results-section mitre-section">
        <div className="section-header-box green">
          <h2>Attack Matrix</h2>
          <div className="count-badge">{activeChainNodes.length}</div>
        </div>

        <div className="attack-path-visualization-box detailed-outline" style={{ border: '3px solid var(--scan-text)', background: 'var(--scan-panel)', padding: '1.15rem', marginBottom: '1.5rem', boxShadow: 'var(--scan-shadow-sm)' }}>
          <div className="attack-path-visualization-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px solid var(--scan-text)', paddingBottom: '0.65rem', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-3xl)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Tactical Retro Attack Path</h3>
              <p style={{ margin: '0.2rem 0 0', fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-sm)', color: 'var(--scan-muted-text)' }}>
                {attackChainData.chain_summary || "Live threat execution chain traced from target entry points through MITRE ATT&CK techniques to business impact"}
              </p>
            </div>
            
            {attackChainData.alternate_chain && (
              <div className="chain-toggle-group" style={{ display: 'flex', gap: '0.5rem', border: '2px solid var(--scan-text)', padding: '0.2rem', background: 'var(--scan-bg)' }}>
                <button 
                  onClick={() => setActiveChain('primary')}
                  style={{ background: activeChain === 'primary' ? 'var(--scan-text)' : 'transparent', color: activeChain === 'primary' ? 'var(--scan-bg)' : 'var(--scan-text)', border: 'none', padding: '0.4rem 0.8rem', fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontWeight: 'bold' }}>
                  PRIMARY PATH
                </button>
                <button 
                  onClick={() => setActiveChain('alternate')}
                  title={attackChainData.alternate_chain_label || 'Alternate Path'}
                  style={{ background: activeChain === 'alternate' ? 'var(--scan-text)' : 'transparent', color: activeChain === 'alternate' ? 'var(--scan-bg)' : 'var(--scan-text)', border: 'none', padding: '0.4rem 0.8rem', fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontWeight: 'bold' }}>
                  ALTERNATE PATH
                </button>
              </div>
            )}
          </div>
          
          <div className="svg-responsive-wrapper" style={{ width: '100%', overflowX: 'auto', background: 'var(--scan-card)', border: '3px solid var(--scan-text)', padding: '0.6rem 0' }}>
            <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" height={svgHeight} style={{ minWidth: `${svgWidth}px`, display: 'block' }}>
              <defs>
                <style>{`
                  @keyframes laser-sweep {
                    to { stroke-dashoffset: -24; }
                  }
                  .laser-line-anim {
                    stroke: var(--scan-text);
                    stroke-width: 3;
                    stroke-dasharray: 8, 4;
                  }
                  .pulse { animation: pulse 1.5s infinite; }
                  @keyframes pulse { 0% {opacity: 1;} 50% {opacity: 0.5;} 100% {opacity: 1;} }
                `}</style>
                <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--scan-text)" />
                </marker>
              </defs>
              
              {/* Column Headers */}
              {colHeaders.map((header, colIdx) => (
                <text
                  key={`header-${colIdx}`}
                  x={columnsX[colIdx]}
                  y={40}
                  textAnchor="middle"
                  style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', fill: 'var(--scan-muted-text)' }}
                >
                  {header}
                </text>
              ))}

              {/* Render Connection Lines and Dwell Times */}
              {connections.map(conn => {
                let pathD = "";
                let midX = 0;
                let midY = 0;
                
                if (conn.from.cx === conn.to.cx) {
                  const x1 = conn.from.cx;
                  const y1 = conn.from.cy + nodeH / 2;
                  const x2 = conn.to.cx;
                  const y2 = conn.to.cy - nodeH / 2 - 10;
                  pathD = `M ${x1} ${y1} L ${x2} ${y2}`;
                  midX = x1;
                  midY = (y1 + y2) / 2 - 10;
                } else {
                  const x1 = conn.from.cx + nodeW / 2;
                  const y1 = conn.from.cy;
                  const x2 = conn.to.cx - nodeW / 2;
                  const y2 = conn.to.cy;
                  pathD = `M ${x1} ${y1} C ${(x1 + x2)/2} ${y1}, ${(x1 + x2)/2} ${y2}, ${x2 - 10} ${y2}`;
                  midX = (x1 + x2) / 2;
                  midY = (y1 + y2) / 2 - 10;
                }
                
                // Calculate speed based on confidence (0.0 to 1.0)
                const conf = Math.min(1.0, Math.max(0.0, conn.confidence || 0.6));
                const animDuration = `${Math.max(0.3, 2.0 - conf)}s`;
                
                return (
                  <g key={conn.id}>
                    <path
                      d={pathD}
                      className="laser-line-anim"
                      fill="none"
                      style={{ animation: `laser-sweep ${animDuration} linear infinite` }}
                      strokeOpacity={Math.max(0.3, conf)}
                      markerEnd="url(#arrowhead)"
                    />
                    {conn.dwell && conn.dwell !== 'Unknown' && (
                      <text
                        x={midX}
                        y={midY}
                        textAnchor="middle"
                        style={{ fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 'bold', fill: 'var(--scan-highlight)', background: 'var(--scan-bg)' }}
                      >
                        ⏱ {conn.dwell}
                      </text>
                    )}
                  </g>
                );
              })}
              
              {/* Render Node Boxes */}
              {pathNodes.flat().map(node => (
                <g key={node.id}>
                  <title>{node.tooltip || node.description || node.val}</title>
                  <rect
                    x={node.x + 4}
                    y={node.y + 4}
                    width={nodeW}
                    height={nodeH}
                    fill="rgba(18, 20, 23, 0.15)"
                    rx="0"
                  />
                  <rect
                    x={node.x}
                    y={node.y}
                    width={nodeW}
                    height={nodeH}
                    fill={node.color}
                    stroke="var(--scan-text)"
                    strokeWidth="3"
                    strokeDasharray={node.isDashed ? "6 4" : "none"}
                    rx="0"
                  />
                  <text
                    x={node.cx}
                    y={node.y + 26}
                    textAnchor="middle"
                    style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-bold)', fill: 'var(--scan-text)' }}
                  >
                    {node.label}
                  </text>
                  <text
                    x={node.cx}
                    y={node.y + 46}
                    textAnchor="middle"
                    textLength={node.val.length > 20 ? nodeW - 20 : undefined}
                    lengthAdjust={node.val.length > 20 ? "spacingAndGlyphs" : undefined}
                    style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-bold)', fill: 'var(--scan-text)' }}
                  >
                    {node.val}
                  </text>
                </g>
              ))}
            </svg>
          </div>
          
          <div className="attack-path-legend" style={{ display: 'flex', gap: '1.25rem', marginTop: '0.85rem', flexWrap: 'wrap', fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-base)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '12px', height: '12px', border: '2px solid var(--scan-text)', background: tacticColors['Initial Access'] }}></div>
              <span>Entry Vector</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '12px', height: '12px', border: '2px solid var(--scan-text)', background: tacticColors['Impact'] }}></div>
              <span>Critical Impact</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '12px', height: '12px', border: '2px solid var(--scan-text)', strokeDasharray: '4 2', fill: 'transparent' }}></div>
              <span>High Detectability</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '12px', height: '12px', border: '2px solid var(--scan-text)', background: '#CE93D8' }}></div>
              <span>MITRE Technique</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '12px', height: '12px', border: '2px solid var(--scan-text)', background: '#A5D6A7' }}></div>
              <span>Secure Perimeter</span>
            </div>
          </div>
        </div>

        {techniques.length > 0 ? (
          <div className="key-features-grid">
            {techniques.map((technique, index) => (
              <div key={index} className={`feature-note tactic-${getTacticTone(technique.tactic)}`}>
                <div className="note-header">
                  <h3>Technique {index + 1}:</h3>
                </div>
                <div className="note-content">
                  <div className="technique-id-display">{technique.id || 'N/A'}</div>
                  <h4>{technique.name || 'Unknown Technique'}</h4>
                  <p className="tactic-label">Tactic: <span>{technique.tactic || 'Not specified'}</span></p>
                  {technique.description && (
                    <details className="technique-description-details">
                      <summary>View mapping context</summary>
                      <p className="technique-desc">{technique.description}</p>
                    </details>
                  )}
                  {technique.confidence && (
                    <p className="confidence-label">Confidence: {Math.round(technique.confidence * 100)}%</p>
                  )}
                  <div className="technique-actions">
                    {technique.url && (
                      <a
                        className="technique-action-button"
                        href={technique.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View in ATT&CK
                      </a>
                    )}
                    <button
                      type="button"
                      className="technique-action-button secondary"
                      onClick={() => handleTechniquePivot(technique)}
                    >
                      Find Related Threats
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state-card mitre-empty">
            <div className="empty-state-header">
              <div className="empty-state-box">MATRIX</div>
            </div>
            <div className="empty-state-content">
              <h3>No Techniques Mapped</h3>
              <p>Run a new scan with vulnerability detection to see attack technique mappings aligned with MITRE ATT&CK framework.</p>
              <div className="empty-state-suggestions">
                <span className="suggestion-label">Suggestions:</span>
                <ul>
                  <li>Execute a Deep or Standard scan mode for comprehensive coverage</li>
                  <li>Ensure target website is accessible and responsive</li>
                  <li>Verify all scanner modules are enabled in scan settings</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderRiskSection = () => {
    const risk = normalizedRisk;
    const totalFindings = vulnerabilityStats.total;
    const riskLevelDisplay = (risk.risk_level || 'Unknown').toString();
    console.log('[RENDER] Rendering risk section. Risk:', risk, 'Vulnerabilities:', totalFindings);

    // Threat intel data for detailed display
    const threatIntel = results.threat_intel || {};
    const reputation = threatIntel.reputation || {};
    const vt = threatIntel.virustotal || {};
    const gsb = threatIntel.google_safe_browsing || {};
    const abuse = threatIntel.abuseipdb || {};
    const shodan = threatIntel.shodan || {};
    const sectrails = threatIntel.securitytrails || {};
    const leakLookup = threatIntel.leak_lookup || {};
    const vulners = threatIntel.vulners || {};
    const target = threatIntel.target || {};
    const riskIndicators = threatIntel.risk_indicators || [];
    const hasThreatIntel = threatIntel.data_sources_queried > 0;
    const criticalCount = normalizedRisk.critical_count || 0;
    const highCount = normalizedRisk.high_count || 0;
    const intelSourcesQueried = threatIntel.data_sources_queried || 0;

    const getReputationColorClass = (score) => {
      if (score >= 70) return 'green-bg';
      if (score >= 40) return 'yellow-bg';
      return 'coral-bg';
    };

    return (
      <section className="results-section overview">
        <div className="section-header-box pink">
          <h2>Recon Report</h2>
          <div className="section-count">{totalFindings}</div>
        </div>
        <div className="recon-header-meta">
          <span className="recon-meta-pill">{pluralize(totalFindings, 'finding')}</span>
          <span className="recon-meta-pill">{pluralize(criticalCount, 'critical issue')}</span>
          <span className="recon-meta-pill">{pluralize(highCount, 'high issue')}</span>
          <span className="recon-meta-pill">{pluralize(intelSourcesQueried, 'intel source')} queried</span>
        </div>

        {(reconReportSection.health_grade || reconReportSection.supplier_risk || reconReportSection.exposure_findings) && (
          <div className="overview-grid scan-tab-brief-grid recon-grid-primary">
            <div className="overview-card recon-kpi-card yellow-bg">
              <div className="card-number">01</div>
              <h3 className="card-title">Health Grade</h3>
              <p className="card-content">{reconReportSection.health_grade || 'N/A'} (trend: {reconReportSection?.benchmark?.trend_vs_previous_period || 'pending'})</p>
            </div>
            <div className="overview-card recon-kpi-card coral-bg">
              <div className="card-number">03</div>
              <h3 className="card-title">Exposure Flags</h3>
              <p className="card-content">
                Open ports: {(reconReportSection?.exposure_findings?.insecure_open_ports || []).length}
              </p>
            </div>
          </div>
        )}

        <div className="overview-grid recon-grid-secondary">
          {overviewCards.map((card) => (
            <div key={card.key} className={`overview-card recon-summary-card ${card.color}-bg`}>
              <div className="card-number">{card.number}</div>
              <h3 className="card-title">{card.title}</h3>
              <p className="card-content">{card.description}</p>
            </div>
          ))}
        </div>


        <div className="recon-risk-intel-layout">
          <div className="risk-score-display recon-score-panel">
            <h3 className="intel-section-title">Risk Signal</h3>
            <div className="risk-score-content">
              <div className="score-circle-large" style={{ background: getRiskColor(risk.overall_risk_score) }}>
                {Number.isFinite(risk.overall_risk_score) ? risk.overall_risk_score.toFixed(1) : 'N/A'}
              </div>
              <div className="risk-label">
                <span className="risk-label-level">{riskLevelDisplay}</span>
                <span className="risk-label-caption">Risk Level</span>
              </div>
            </div>
          </div>

          {/* Threat Intelligence Details Section */}
          {hasThreatIntel && (
            <div className="threat-intel-details recon-intel-panel">
              <h3 className="intel-section-title">External Intelligence Sources</h3>

              <div className="intel-source-grid">
                {/* Reputation Score */}
                {reputation.score !== undefined && (
                  <div
                    className={`intel-source-card ${getReputationColorClass(reputation.score)}`}
                    title="Aggregated reputation score from multiple threat intelligence sources. Scores below 40 indicate high risk, 40-70 moderate risk, and above 70 low risk."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">Reputation Score</span>
                    </div>
                    <div className="intel-source-value">{reputation.score}/100</div>
                    <div className="intel-source-detail">{reputation.risk_level || 'Unknown'} • {reputation.sources_checked || 0} sources</div>
                  </div>
                )}

                {/* VirusTotal */}
                {(vt.malicious !== undefined || vt.harmless !== undefined) && (
                  <div
                    className={`intel-source-card ${vt.malicious > 0 ? 'coral-bg' : vt.suspicious > 0 ? 'yellow-bg' : 'green-bg'}`}
                    title="VirusTotal aggregates results from 70+ antivirus engines and URL/domain scanners. Malicious detections indicate the target was flagged as harmful by security vendors."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">VirusTotal</span>
                    </div>
                    <div className="intel-source-value">
                      {vt.malicious > 0 ? `${vt.malicious} Malicious` : vt.suspicious > 0 ? `${vt.suspicious} Suspicious` : 'Clean'}
                    </div>
                    <div className="intel-source-detail">{vt.total_engines || 0} engines scanned</div>
                  </div>
                )}

                {/* Google Safe Browsing */}
                {gsb.status && (
                  <div
                    className={`intel-source-card ${gsb.is_flagged ? 'coral-bg' : 'green-bg'}`}
                    title="Google Safe Browsing checks URLs against Google's constantly updated lists of unsafe web resources including malware, phishing, and unwanted software sites."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">Safe Browsing</span>
                    </div>
                    <div className="intel-source-value">{gsb.is_flagged ? 'FLAGGED' : 'Safe'}</div>
                    <div className="intel-source-detail">{gsb.is_flagged ? gsb.threat_types?.join(', ') || 'Threat detected' : 'No threats found'}</div>
                  </div>
                )}

                {/* AbuseIPDB */}
                {abuse.ip_address && (
                  <div
                    className={`intel-source-card ${abuse.abuse_confidence_score > 50 ? 'coral-bg' : abuse.abuse_confidence_score > 25 ? 'yellow-bg' : 'green-bg'}`}
                    title="AbuseIPDB is a crowd-sourced IP address abuse database. The confidence score indicates the likelihood that the IP is involved in malicious activity based on user reports."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">AbuseIPDB</span>
                    </div>
                    <div className="intel-source-value">{abuse.abuse_confidence_score || 0}%</div>
                    <div className="intel-source-detail">{abuse.total_reports || 0} reports • {abuse.isp || 'Unknown ISP'}</div>
                  </div>
                )}

                {/* Shodan */}
                {shodan.ip && (
                  <div
                    className={`intel-source-card ${shodan.vuln_count > 0 ? 'coral-bg' : 'cyan-bg'}`}
                    title="Shodan is a search engine for Internet-connected devices. It reveals open ports, running services, and known vulnerabilities on the target's IP address."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">Shodan</span>
                    </div>
                    <div className="intel-source-value">{shodan.open_ports_count || 0} Ports</div>
                    <div className="intel-source-detail">{shodan.vuln_count || 0} vulns • {shodan.services?.length || 0} services</div>
                  </div>
                )}

                {/* SecurityTrails */}
                {sectrails.subdomains_count !== undefined && (
                  <div
                    className="intel-source-card blue-bg"
                    title="SecurityTrails provides DNS intelligence including historical records, subdomains, and domain ownership data to map the target's infrastructure."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">SecurityTrails</span>
                    </div>
                    <div className="intel-source-value">{sectrails.subdomains_count || 0} Subdomains</div>
                    <div className="intel-source-detail">DNS records available</div>
                  </div>
                )}

                {/* Leak Lookup */}
                {leakLookup.status && (
                  <div
                    className={`intel-source-card ${leakLookup.breaches_found ? 'coral-bg' : 'green-bg'}`}
                    title="Leak Lookup searches known data breach databases to check if the domain or associated accounts have been compromised in past security incidents."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">Breach Check</span>
                    </div>
                    <div className="intel-source-value">{leakLookup.breaches_found ? 'Breached' : 'No Breaches'}</div>
                    <div className="intel-source-detail">{leakLookup.breach_sources?.length || 0} sources checked</div>
                  </div>
                )}

                {/* Vulners Exploit Database */}
                {vulners.total_exploits !== undefined && (
                  <div
                    className={`intel-source-card ${vulners.total_exploits > 0 ? 'coral-bg' : 'green-bg'}`}
                    title="Vulners is a vulnerability database that aggregates exploits, security advisories, and CVE data. Finding exploits means attackers have ready-to-use attack code."
                  >
                    <div className="intel-source-header">
                      <span className="intel-source-name">Vulners Exploits</span>
                    </div>
                    <div className="intel-source-value">{vulners.total_exploits || 0} Exploits</div>
                    <div className="intel-source-detail">
                      {vulners.cves_searched?.length || 0} CVEs searched • {vulners.vulnerabilities_found || 0} vulns
                    </div>
                  </div>
                )}
              </div>

              {/* Risk Indicators */}
              {riskIndicators.length > 0 && (
                <div className="risk-indicators-section">
                  <h4 className="risk-indicators-title">Active Risk Indicators</h4>
                  <div className="risk-indicators-compact">
                    {riskIndicators.slice(0, 5).map((indicator, idx) => (
                      <div key={idx} className={`risk-indicator-chip ${indicator.severity}`}>
                        <span className="indicator-source-tag">{indicator.source}</span>
                        <span className="indicator-detail">{indicator.type}: {indicator.details}</span>
                      </div>
                    ))}
                    {riskIndicators.length > 5 && (
                      <div className="risk-indicator-chip info">+{riskIndicators.length - 5} more indicators</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderExecutiveSummary = () => {
    // Use detailed summary if available, otherwise fall back to brief
    const displayText = detailedText || briefText;
    const summaryParagraphs = prepareSummaryParagraphs(displayText);
    const isLoading = detailedLoading || briefLoading;
    const error = detailedError || briefError;
    const cached = detailedCached || briefCached;
    const topRisks = (missionBriefSection.top_risks || [])
      .map((risk) => risk?.title)
      .filter(Boolean)
      .filter((title) => !/^unknown vulnerability$/i.test(title) && !/might be interesting/i.test(title))
      .slice(0, 2);
    const showMissionCards = Boolean(
      missionBriefSection?.security_posture ||
      topRisks.length ||
      hasUsefulBusinessImpact(missionBriefSection?.business_impact)
    );

    const summaryTabs = [
      {
        id: 'overview',
        label: 'Executive Brief',
        className: 'tab tab-yellow',
        badge: summaryParagraphs.length || (isLoading ? '...' : 0),
        content: () => (
          <div className="summary-disclosure-panel">
            {isLoading && !displayText && (
              <div className="summary-disclosure-panel__meta">
                <span className="summary-status summary-status--inline">Updating analysis…</span>
              </div>
            )}

            {isLoading && !displayText && (
              <div className="loading-container" style={{ margin: '1rem 0', padding: '1rem', border: '2px solid var(--scan-text)', boxShadow: 'none' }}>
                <PixelSpinner size={32} />
                <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: 'var(--font-size-base)'}}>Synthesizing tactical telemetry...</p>
              </div>
            )}

            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px', padding: '2rem' }}>
                <PixelSpinner size={80} />
              </div>
            ) : summaryParagraphs.length > 0 ? (
              <ExpandableContent maxHeight={220} showMoreText="Expand brief" showLessText="Collapse brief">
                <div className="summary-text">
                  <RetroBriefText text={detailedText || briefText} maxLines={detailedText ? 100 : 4} />
                </div>
              </ExpandableContent>
            ) : (
              !isLoading && !error && (
                <p className="summary-status">
                  {detailedFetchAttempted
                    ? 'Summary will appear here once generated.'
                    : 'Click the button below to generate a detailed technical analysis.'}
                </p>
              )
            )}

            {!isLoading && !error && !detailedText && briefText && !detailedFetchAttempted && (
              <div className="summary-disclosure-panel__actions">
                <button 
                  type="button" 
                  onClick={handleLoadDetailedSummary} 
                  className="load-all-button small"
                  style={{ background: 'var(--color-block-coral, #f1563f)' }}
                >
                  Generate Detailed Analysis
                </button>
              </div>
            )}

            {!isLoading && !error && (detailedText || (briefText && detailedFetchAttempted)) && (
              <div className="summary-disclosure-panel__actions" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleLoadDetailedSummary} 
                  className="load-all-button small" 
                  style={{ background: 'var(--color-block-coral, #f1563f)' }}
                >
                  RECOMPILE SITREP
                </button>
              </div>
            )}

            {!isLoading && error && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
                <button 
                  type="button" 
                  onClick={handleSummaryRetry} 
                  className="load-all-button"
                  style={{ background: 'var(--color-block-coral, #f1563f)' }}
                  disabled={detailedRetryInProgress}
                >
                  {detailedRetryInProgress ? 'Retrying...' : `Retry Analysis ${detailedRetryAttempts > 0 ? `(${detailedRetryAttempts}/3)` : ''}`}
                </button>
              </div>
            )}
          </div>
        )
      },
      {
        id: 'signals',
        label: 'Decision Signals',
        className: 'tab tab-pink',
        badge: missionBriefBullets.length,
        content: () => (
          <div className="summary-disclosure-panel">
            <div className="summary-disclosure-panel__meta">
              <span className="summary-status">What the executive audience should notice first.</span>
            </div>
            <ul className="summary-signal-list">
              {missionBriefBullets.map((bullet, index) => (
                <li key={`${index}-${bullet}`}>{bullet}</li>
              ))}
            </ul>
            {topRisks.length > 0 && (
              <div className="summary-signal-footnote">
                Top risks: {topRisks.join(' • ')}
              </div>
            )}
          </div>
        )
      },
      {
        id: 'metrics',
        label: 'Metrics',
        className: 'tab tab-coral',
        badge: vulnerabilityStats.total,
        content: () => (
          <div className="summary-disclosure-panel">
            <div className="summary-metrics-grid">
              <div className="summary-metric-card cyan-bg">
                <div className="summary-metric-card__value">{normalizedRisk.overall_risk_score ?? 0}</div>
                <div className="summary-metric-card__label">Risk score</div>
              </div>
              <div className="summary-metric-card pink-bg">
                <div className="summary-metric-card__value">{vulnerabilityStats.total}</div>
                <div className="summary-metric-card__label">Total findings</div>
              </div>
              <div className="summary-metric-card coral-bg">
                <div className="summary-metric-card__value">{(vulnerabilityStats.counts.critical || 0) + (vulnerabilityStats.counts.high || 0)}</div>
                <div className="summary-metric-card__label">Critical + High</div>
              </div>
              <div className="summary-metric-card green-bg">
                <div className="summary-metric-card__value">{cached ? 'Cached' : 'Live'}</div>
                <div className="summary-metric-card__label">Summary source</div>
              </div>
            </div>
          </div>
        )
      }
    ];

    return (
      <section className="results-section summary-section">
        <div className="section-header-box yellow">
          <h2>Mission Brief</h2>
          <div className="count-badge">{cached ? 'Cached' : 'Live'}</div>
        </div>
        <div className="executive-summary-card">
          <TabbedInterface
            tabs={summaryTabs}
            defaultTab="overview"
            className="summary-disclosure-tabs tabs-shell"
          />
        </div>
      </section>
    );
  };

  const renderAIAnalysis = () => {
    const aiInsights = results.ai_analysis || [];
    console.log('[RENDER] Rendering intelligence section. AI insights:', aiInsights.length, 'Vulnerabilities:', vulnerabilityStats.total);

    const severityBuckets = vulnerabilityStats.grouped;
    const strategiesTimeline = results.remediation_strategies?.timeline || {};

    // Calculate average CVSS for each severity bucket for dynamic effort estimation
    const calculateBucketAvgCvss = (items) => {
      if (!items || items.length === 0) return 0;
      const validScores = items.filter(v => typeof v.cvss_score === 'number' && !Number.isNaN(v.cvss_score));
      if (validScores.length === 0) return 0;
      return validScores.reduce((sum, v) => sum + v.cvss_score, 0) / validScores.length;
    };

    // Dynamic priority configuration - labels will be generated based on actual data
    const priorityConfig = {
      critical: { baseLabel: 'Critical', className: 'cyan', accentColor: '#6DD4D9', symbol: '!', urgency: 'Immediate' },
      high: { baseLabel: 'High', className: 'coral', accentColor: '#FF6B6B', symbol: 'H', urgency: 'Urgent' },
      medium: { baseLabel: 'Medium', className: 'pink', accentColor: '#E39FCE', symbol: 'M', urgency: 'Planned' },
      low: { baseLabel: 'Low', className: 'yellow', accentColor: '#FFD93D', symbol: 'L', urgency: 'Scheduled' },
      info: { baseLabel: 'Info', className: 'blue', accentColor: '#64B5F6', symbol: 'i', urgency: 'Monitor' }
    };

    const mapInsightToPriority = (insight) => {
      const prioritySource = `${insight.priority || ''} ${insight.priority_key || ''} ${insight.priority_level || ''} ${insight.remediation_priority || ''} ${insight.timeframe || ''} ${insight.window || ''} ${insight.urgency || ''}`.toLowerCase();
      if (prioritySource.includes('critical') || prioritySource.includes('immediate') || prioritySource.includes('now')) return 'critical';
      if (prioritySource.includes('high') || prioritySource.includes('urgent') || prioritySource.includes('week')) return 'high';
      if (prioritySource.includes('medium') || prioritySource.includes('month') || prioritySource.includes('quarter') || prioritySource.includes('sprint')) return 'medium';
      if (prioritySource.includes('info') || prioritySource.includes('informational') || prioritySource.includes('monitor')) return 'info';
      return 'low';
    };

    const insightsByPriority = {
      critical: [],
      high: [],
      medium: [],
      low: [],
      info: []
    };

    aiInsights.forEach((insight) => {
      const priority = mapInsightToPriority(insight);
      insightsByPriority[priority].push(insight);
    });

    // Dynamic bucket configuration - no more static defaultDuration values
    const bucketsConfig = [
      { key: 'critical', timelineKey: 'immediate_action', effortMultiplier: 1.5 },
      { key: 'high', timelineKey: 'short_term', effortMultiplier: 1.2 },
      { key: 'medium', timelineKey: 'medium_term', effortMultiplier: 1.0 },
      { key: 'low', timelineKey: 'long_term', effortMultiplier: 0.8 },
      { key: 'info', timelineKey: 'monitoring', effortMultiplier: 0.3 }
    ];

    let currentStart = 0;
    const timelineData = [];

    bucketsConfig.forEach(({ key, timelineKey, effortMultiplier }) => {
      const severityItems = severityBuckets[key] || [];
      const bucketInsights = insightsByPriority[key] || [];
      const timelineInfo = strategiesTimeline[timelineKey];
      const hasContent = severityItems.length || bucketInsights.length || timelineInfo;

      if (!hasContent) {
        return;
      }

      // Calculate dynamic duration based on actual item count and CVSS
      const bucketAvgCvss = calculateBucketAvgCvss(severityItems);
      const itemCount = severityItems.length + Math.ceil(bucketInsights.length * 0.5); // Insights count as partial items
      const duration = computeTimelineDuration(timelineInfo, 7, itemCount, bucketAvgCvss);
      const bucketStart = currentStart;
      currentStart += duration;

      const priorityToken = priorityConfig[key];

      // Generate dynamic label based on actual week numbers
      const weekLabel = getDynamicLabel(bucketStart, bucketStart + duration, key);
      const dynamicLabel = `${priorityToken.urgency} (${weekLabel})`;

      // Calculate estimated hours for display
      const estimatedHours = itemCount * (bucketAvgCvss >= 7 ? 8 : bucketAvgCvss >= 4 ? 4 : 2) * effortMultiplier;

      timelineData.push({
        key,
        label: dynamicLabel,
        baseLabel: priorityToken.baseLabel,
        barClass: priorityToken.className,
        accentColor: priorityToken.accentColor,
        symbol: priorityToken.symbol,
        items: severityItems,
        insights: bucketInsights,
        duration,
        start: bucketStart,
        estimatedHours: Math.round(estimatedHours),
        avgCvss: bucketAvgCvss,
        milestoneTarget: Math.round(bucketStart + duration),
        milestoneLabel: `Day ${Math.round(bucketStart + duration)} (~${Math.round(estimatedHours)}h effort)`
      });
    });

    // Calculate total effort for summary
    const totalEstimatedHours = timelineData.reduce((sum, bucket) => sum + (bucket.estimatedHours || 0), 0);
    const totalDuration = timelineData.reduce((max, entry) => Math.max(max, entry.start + entry.duration), 0) || 1;
    const totalWeeks = Math.ceil(totalDuration / 7);

    // Create clean week-based header labels (1-indexed for display)
    const totalWeeksForHeader = Math.max(1, Math.ceil(totalDuration / 7));
    const headerLabels = Array.from({ length: totalWeeksForHeader }, (_, index) => ({
      id: index + 1,
      week: index + 1,
      dayStart: (index * 7) + 1,
      dayEnd: Math.min((index + 1) * 7, Math.ceil(totalDuration))
    }));

    // Filter out empty priority levels (0 findings)
    const filteredTimelineData = timelineData.filter(t => t.items.length > 0);
    const timelineFindingCount = filteredTimelineData.reduce((sum, bucket) => sum + bucket.items.length, 0);

    return (
      <section className="results-section ai-timeframes">
        <div className="section-header-box purple">
          <h2>Intel Analysis</h2>
          <div className="count-badge">{timelineFindingCount || aiInsights.length || vulnerabilityStats.total}</div>
        </div>

        {(intelAnalysisSection.patch_intelligence || intelAnalysisSection.coverage || intelAnalysisSection.security_roadmap) && (
          <div className="overview-grid scan-tab-brief-grid">
            <div className="overview-card cyan-bg">
              <div className="card-number">01</div>
              <h3 className="card-title">Patch Latency</h3>
              <p className="card-content">
                {intelAnalysisSection?.patch_intelligence?.average_patch_latency_days ?? 'N/A'} days average
              </p>
            </div>
            <div className="overview-card yellow-bg">
              <div className="card-number">02</div>
              <h3 className="card-title">Coverage</h3>
              <p className="card-content">
                EDR: {intelAnalysisSection?.coverage?.edr_coverage_percent ?? 'N/A'}% | AV: {intelAnalysisSection?.coverage?.antivirus_coverage_percent ?? 'N/A'}%
              </p>
            </div>
            <div className="overview-card pink-bg">
              <div className="card-number">03</div>
              <h3 className="card-title">Roadmap</h3>
              <p className="card-content">
                Zero Trust: {intelAnalysisSection?.security_roadmap?.zero_trust_progress ?? 'N/A'}
              </p>
            </div>
          </div>
        )}

        <div className="threat-intel-details">
          <h3 className="intel-section-title">Patch Intelligence</h3>
          <div className="intel-source-grid">
            <div className="intel-source-card cyan-bg">
              <div className="intel-source-header">
                <span className="intel-source-name">Average Patch Latency</span>
              </div>
              <div className="intel-source-value">{intelAnalysisSection?.patch_intelligence?.average_patch_latency_days ?? 'N/A'}</div>
              <div className="intel-source-detail">days</div>
            </div>
            <div className="intel-source-card coral-bg">
              <div className="intel-source-header">
                <span className="intel-source-name">Missing Critical Patches</span>
              </div>
              <div className="intel-source-value">{(intelAnalysisSection?.patch_intelligence?.missing_critical_patches || []).length}</div>
              <div className="intel-source-detail">high/critical CVEs pending</div>
            </div>
          </div>
          {(intelAnalysisSection?.patch_intelligence?.missing_critical_patches || []).length > 0 && (
            <div className="risk-indicators-section">
              <h4 className="risk-indicators-title">Critical Patch Backlog</h4>
              <div className="risk-indicators-compact">
                {intelAnalysisSection.patch_intelligence.missing_critical_patches.slice(0, 8).map((item, idx) => (
                  <div key={`${item.cve_id || item.title || 'patch'}-${idx}`} className="risk-indicator-chip high">
                    <span className="indicator-source-tag">{item.cve_id || 'CVE'}</span>
                    <span className="indicator-detail">{item.asset || 'Asset'} - {item.severity || 'unknown'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="threat-intel-details">
          <h3 className="intel-section-title">Coverage & Roadmap</h3>
          <div className="intel-source-grid">
            <div className="intel-source-card green-bg">
              <div className="intel-source-header">
                <span className="intel-source-name">EDR Coverage</span>
              </div>
              <div className="intel-source-value">{intelAnalysisSection?.coverage?.edr_coverage_percent ?? 'N/A'}%</div>
              <div className="intel-source-detail">endpoint telemetry</div>
            </div>
            <div className="intel-source-card yellow-bg">
              <div className="intel-source-header">
                <span className="intel-source-name">Antivirus Coverage</span>
              </div>
              <div className="intel-source-value">{intelAnalysisSection?.coverage?.antivirus_coverage_percent ?? 'N/A'}%</div>
              <div className="intel-source-detail">asset protection</div>
            </div>
            <div className="intel-source-card pink-bg">
              <div className="intel-source-header">
                <span className="intel-source-name">Zero Trust Progress</span>
              </div>
              <div className="intel-source-value">{intelAnalysisSection?.security_roadmap?.zero_trust_progress ?? 'N/A'}</div>
              <div className="intel-source-detail">roadmap status</div>
            </div>
          </div>
          {(intelAnalysisSection?.coverage?.blind_spots || []).length > 0 && (
            <div className="risk-indicators-section">
              <h4 className="risk-indicators-title">Coverage Blind Spots</h4>
              <div className="risk-indicators-compact">
                {(intelAnalysisSection.coverage.blind_spots || []).slice(0, 6).map((spot, idx) => (
                  <div key={`spot-${idx}`} className="risk-indicator-chip medium">
                    <span className="indicator-source-tag">Coverage</span>
                    <span className="indicator-detail">{spot}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dynamic Summary Stats */}
        <div className="intel-summary-stats">
          <div className="stat-card">
            <span className="stat-value">{timelineFindingCount}</span>
            <span className="stat-label">Total Findings</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{totalWeeks}</span>
            <span className="stat-label">Est. Weeks</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{totalEstimatedHours}h</span>
            <span className="stat-label">Est. Effort</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{timelineData.length}</span>
            <span className="stat-label">Priority Levels</span>
          </div>
        </div>

        {filteredTimelineData.length > 0 ? (
          <div className="gantt-wrapper">
            {/* Gantt Chart Legend */}
            <div className="gantt-legend">
              <div className="legend-item">
                <div className="legend-bar cyan-bg"></div>
                <span>Critical</span>
              </div>
              <div className="legend-item">
                <div className="legend-bar coral-bg"></div>
                <span>High</span>
              </div>
              <div className="legend-item">
                <div className="legend-bar pink-bg"></div>
                <span>Medium</span>
              </div>
              <div className="legend-item">
                <div className="legend-bar yellow-bg"></div>
                <span>Low</span>
              </div>
              <div className="legend-item">
                <div className="legend-bar blue-bg"></div>
                <span>Info</span>
              </div>
              <div className="legend-item">
                <div className="legend-milestone"></div>
                <span>Milestone</span>
              </div>
            </div>

            <div className="gantt-container">
              {/* Timeline Header */}
              <div className="gantt-header">
                <div className="gantt-row-label">
                  <span className="label-title">Priority</span>
                  <span className="label-subtitle">Findings / Effort</span>
                </div>
                <div className="gantt-timeline-header">
                  {headerLabels.map((label) => (
                    <div key={label.id} className="gantt-time-label">
                      <span className="week-label">Week {label.week}</span>
                      <span className="day-range">Days {label.dayStart}-{label.dayEnd}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline Body */}
              <div className="gantt-body">
                {filteredTimelineData.map((timeline, index) => {
                  const itemCount = timeline.items.length;
                  const barStartPercent = (timeline.start / totalDuration) * 100;
                  const barWidthPercent = Math.max((timeline.duration / totalDuration) * 100, 8);
                  const endDay = Math.round(timeline.start + timeline.duration);

                  return (
                    <div key={timeline.key} className={`gantt-row ${index % 2 === 0 ? 'even' : 'odd'}`}>
                      {/* Row Label */}
                      <div className="gantt-row-label">
                        <div className="priority-header">
                          <span className="priority-badge" style={{ background: timeline.accentColor }}>
                            {timeline.symbol}
                          </span>
                          <div className="priority-text">
                            <strong>{timeline.baseLabel || timeline.key}</strong>
                            <span className="priority-subtitle">
                              {itemCount} {pluralize(itemCount, 'finding')}
                            </span>
                            <span className="priority-effort">
                              ~{timeline.estimatedHours || 0}h effort
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Bar Area */}
                      <div className="gantt-bars">
                        {/* Main Bar */}
                        <div
                          className={`gantt-bar ${timeline.barClass}-bg`}
                          style={{
                            left: `${barStartPercent}%`,
                            width: `${barWidthPercent}%`
                          }}
                          title={`${timeline.baseLabel}: Days ${Math.round(timeline.start + 1)}-${endDay}, ${itemCount} findings, ${timeline.estimatedHours}h effort`}
                        >
                          <span className="bar-text">
                            {timeline.duration}d / {timeline.estimatedHours}h
                          </span>
                        </div>

                        {/* End Milestone */}
                        <div
                          className="gantt-milestone"
                          style={{ left: `${Math.min((endDay / totalDuration) * 100, 98)}%` }}
                          title={`Complete by Day ${endDay}`}
                        >
                          <span className="milestone-text">D{endDay}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary Footer */}
              <div className="gantt-footer">
                <div className="gantt-footer-label">
                  <strong>Summary</strong>
                </div>
                <div className="gantt-footer-content">
                  <div className="footer-stats">
                    <span className="footer-stat">
                      <strong>{totalDuration}</strong> days
                    </span>
                    <span className="footer-stat">
                      <strong>{totalEstimatedHours}</strong> hours
                    </span>
                    <span className="footer-stat">
                      <strong>{timelineFindingCount}</strong> findings
                    </span>
                    <span className="footer-stat">
                      <strong>{filteredTimelineData.length}</strong> priorities
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state-card ai-empty">
            <div className="empty-state-header">
              <div className="empty-state-box">TIMELINE</div>
            </div>
            <div className="empty-state-content">
              <h3>No Remediation Data</h3>
              <p>Timeline visualization will populate once the scan identifies actionable findings to prioritize.</p>
              <div className="empty-state-suggestions">
                <span className="suggestion-label">Next Steps:</span>
                <ul>
                  <li>Launch a new scan to generate vulnerability findings</li>
                  <li>Target may be fully patched - run Deep scan for thorough assessment</li>
                  <li>Check that target allows automated scanning without restrictions</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {aiInsights.length > 0 ? (
          <div className="ai-insights-section">
            <h3>Expert Insights & Recommendations</h3>
            <p className="insights-description">
              Insights align with the timeline above. Each recommendation updates as new scan data arrives.
            </p>
            <div className="insights-grid">
              {aiInsights.map((insight, index) => {
                const priority = mapInsightToPriority(insight);
                const priorityToken = priorityConfig[priority] || priorityConfig.low;

                return (
                  <div key={index} className="insight-card-modern">
                    <span className="insight-priority-tag" style={{ background: priorityToken.accentColor }}>
                      {priorityToken.symbol}
                    </span>
                    <h4>{insight.title || `Insight ${index + 1}`}</h4>
                    <p>{insight.description}</p>
                    {insight.recommendations && insight.recommendations.length > 0 && (
                      <ul className="insight-list">
                        {insight.recommendations.slice(0, 2).map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="ai-insights-section">
            <div className="empty-state-card insights-empty">
              <div className="empty-state-header">
                <div className="empty-state-box">INSIGHTS</div>
              </div>
              <div className="empty-state-content">
                <h3>No Expert Insights Available</h3>
                <p>AI-powered expert insights will be generated once vulnerabilities are identified during scanning.</p>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderRemediationStrategies = () => {
    const strategies = results.remediation_strategies || {};
    const recommendations = strategies.recommendations || [];
    const priorityMatrix = strategies.priority_matrix || {};
    const timeline = strategies.timeline || {};
    
    const rawCostBenefit = (defensePlaybookSection.financial_impact && Object.keys(defensePlaybookSection.financial_impact).length)
      ? {
        total_remediation_cost: defensePlaybookSection.financial_impact.remediation_cost,
        potential_loss: defensePlaybookSection.financial_impact.potential_loss,
        roi_percentage: defensePlaybookSection.financial_impact.roi_percentage
      }
      : (strategies.cost_benefit || {});

    // Normalize keys so they are uniform and robust across ML service and Playbook structures
    const costBenefit = {
      total_remediation_cost: rawCostBenefit.total_remediation_cost ?? rawCostBenefit.estimated_remediation_investment ?? 0,
      potential_loss: rawCostBenefit.potential_loss ?? 0,
      roi_percentage: rawCostBenefit.roi_percentage ?? 0,
      net_benefit: rawCostBenefit.net_benefit ?? ((rawCostBenefit.potential_loss ?? 0) - (rawCostBenefit.total_remediation_cost ?? rawCostBenefit.estimated_remediation_investment ?? 0))
    };

    const resourceAllocation = strategies.resource_allocation || {};

    // Use vulnerabilityStats or normalizedRisk to show actual data if strategies are empty
    const hasStrategies = Object.keys(strategies).length > 0;

    // Get counts from vulnerabilityStats first, then fall back to normalizedRisk (from risk_assessment)
    const criticalCount = vulnerabilityStats.counts.critical || normalizedRisk.critical_count || 0;
    const highCount = vulnerabilityStats.counts.high || normalizedRisk.high_count || 0;
    const mediumCount = vulnerabilityStats.counts.medium || normalizedRisk.medium_count || 0;
    const lowCount = vulnerabilityStats.counts.low || normalizedRisk.low_count || 0;
    const infoCount = vulnerabilityStats.counts.info || 0;
    const totalFromCounts = criticalCount + highCount + mediumCount + lowCount + infoCount;

    // Use vulnerabilityStats.total as it includes ALL vulnerabilities
    const hasVulnerabilities = vulnerabilityStats.total > 0 || totalFromCounts > 0;

    // Build priority matrix from vulnerabilities if not provided
    const effectivePriorityMatrix = Object.keys(priorityMatrix).length > 0
      ? priorityMatrix
      : {
        critical: vulnerabilityStats.grouped.critical || [],
        high: vulnerabilityStats.grouped.high || [],
        medium: vulnerabilityStats.grouped.medium || [],
        low: vulnerabilityStats.grouped.low || [],
        info: vulnerabilityStats.grouped.info || []
      };

    // Calculate totals - use matrix if available, else use counts
    let totalIssues = Object.values(effectivePriorityMatrix).reduce((sum, items) =>
      sum + (Array.isArray(items) ? items.length : 0), 0);
    if (totalIssues === 0) {
      totalIssues = totalFromCounts || vulnerabilityStats.total;
    }

    // Build timeline from vulnerabilities if not provided  
    const effectiveTimeline = Object.keys(timeline).length > 0 ? timeline : {
      immediate_action: vulnerabilityStats.grouped.critical?.length > 0 ? {
        description: `Address ${vulnerabilityStats.grouped.critical.length} critical ${pluralize(vulnerabilityStats.grouped.critical.length, 'vulnerability', 'vulnerabilities')} immediately to prevent active exploitation and data breaches.`,
        items: vulnerabilityStats.grouped.critical.slice(0, 5).map(v => ({
          title: v.title || v.name || 'Critical Vulnerability',
          estimated_hours: Math.max(2, Math.ceil((v.cvss_score || 9) * 0.5)),
          location: v.location || v.url || ''
        }))
      } : null,
      short_term: vulnerabilityStats.grouped.high?.length > 0 ? {
        description: `Fix ${vulnerabilityStats.grouped.high.length} high severity ${pluralize(vulnerabilityStats.grouped.high.length, 'issue')} within the first week to reduce attack surface.`,
        items: vulnerabilityStats.grouped.high.slice(0, 5).map(v => ({
          title: v.title || v.name || 'High Priority Issue',
          estimated_hours: Math.max(2, Math.ceil((v.cvss_score || 7) * 0.6)),
          location: v.location || v.url || ''
        }))
      } : null,
      medium_term: vulnerabilityStats.grouped.medium?.length > 0 ? {
        description: `Address ${vulnerabilityStats.grouped.medium.length} medium severity ${pluralize(vulnerabilityStats.grouped.medium.length, 'issue')} within 2-4 weeks as part of regular maintenance.`,
        items: vulnerabilityStats.grouped.medium.slice(0, 5).map(v => ({
          title: v.title || v.name || 'Medium Priority Issue',
          estimated_hours: Math.max(1, Math.ceil((v.cvss_score || 5) * 0.4)),
          location: v.location || v.url || ''
        }))
      } : null,
      long_term: vulnerabilityStats.grouped.low?.length > 0 ? {
        description: `Plan remediation of ${vulnerabilityStats.grouped.low.length} low severity ${pluralize(vulnerabilityStats.grouped.low.length, 'issue')} during scheduled maintenance windows.`,
        items: vulnerabilityStats.grouped.low.slice(0, 5).map(v => ({
          title: v.title || v.name || 'Low Priority Issue',
          estimated_hours: Math.max(1, Math.ceil((v.cvss_score || 3) * 0.3)),
          location: v.location || v.url || ''
        }))
      } : null,
      hardening: vulnerabilityStats.grouped.info?.length > 0 ? {
        description: `Review ${vulnerabilityStats.grouped.info.length} informational ${pluralize(vulnerabilityStats.grouped.info.length, 'finding')} to improve overall security posture and hardening.`,
        items: vulnerabilityStats.grouped.info.slice(0, 5).map(v => ({
          title: v.title || v.name || 'Security Finding',
          estimated_hours: 1,
          location: v.location || v.url || ''
        }))
      } : null
    };

    // Use centralized sharedMetrics for consistent data across all tabs
    const {
      estimatedCost, potentialLoss, effortHours, roiPercentage,
      weeksNeeded, maxWeeks, estimatedTimeline, budgetRange, teamComposition,
      riskScore, riskLevel
    } = sharedMetrics;

    // Build dynamic recommendation based on actual scan findings
    const buildDynamicRecommendation = () => {
      const parts = [];
      if (criticalCount > 0) {
        parts.push(`${criticalCount} critical ${pluralize(criticalCount, 'vulnerability', 'vulnerabilities')} requiring immediate attention (24-48 hours)`);
      }
      if (highCount > 0) {
        parts.push(`${highCount} high severity ${pluralize(highCount, 'issue')} to address within 1 week`);
      }
      if (mediumCount > 0) {
        parts.push(`${mediumCount} medium ${pluralize(mediumCount, 'issue')} for near-term remediation`);
      }
      if (lowCount > 0) {
        parts.push(`${lowCount} low priority ${pluralize(lowCount, 'item')} for scheduled maintenance`);
      }
      if (infoCount > 0) {
        parts.push(`${infoCount} informational ${pluralize(infoCount, 'finding')} for security hardening`);
      }

      if (parts.length === 0) {
        return 'No significant security issues detected. Continue regular security monitoring.';
      }

      const priorityText = criticalCount > 0 ? 'URGENT: ' : highCount > 0 ? 'Priority: ' : '';
      return `${priorityText}Found ${parts.join(', ')}. Estimated remediation effort: ${effortHours} hours.`;
    };

    const effectiveCostBenefit = Object.keys(costBenefit).length > 0 ? costBenefit : {
      total_remediation_cost: estimatedCost,
      potential_loss: potentialLoss,
      net_benefit: potentialLoss - estimatedCost,
      roi_percentage: roiPercentage,
      effort_hours: effortHours,
      recommendation: buildDynamicRecommendation()
    };

    const effectiveResourceAllocation = Object.keys(resourceAllocation).length > 0 ? resourceAllocation : {
      team_composition: teamComposition,
      estimated_timeline: estimatedTimeline,
      budget_range: budgetRange
    };

    // Build recommendations from vulnerabilities if not provided - using tactical terminology
    const effectiveRecommendations = recommendations.length > 0 ? recommendations : [
      ...(criticalCount > 0 ? [{
        title: `Neutralize ${criticalCount} Critical ${pluralize(criticalCount, 'Threat', 'Threats')}`,
        description: 'PRIORITY ALPHA: Critical threats detected in the perimeter. Immediate tactical response required to prevent hostile breach and data exfiltration.',
        priority: 'critical',
        category: 'Emergency Response',
        action_items: vulnerabilityStats.grouped.critical.slice(0, 3).map(v => v.recommendation || `Neutralize: ${v.title || 'Critical threat'}`),
        estimated_effort: `${criticalCount * 8} hours`
      }] : []),
      ...(highCount > 0 ? [{
        title: `Engage ${highCount} High-Value ${pluralize(highCount, 'Target')}`,
        description: 'PRIORITY BRAVO: High-severity hostiles identified. Deploy countermeasures within the current operational window to secure the perimeter.',
        priority: 'high',
        category: 'Tactical Strike',
        action_items: vulnerabilityStats.grouped.high.slice(0, 3).map(v => v.recommendation || `Engage: ${v.title || 'High-value target'}`),
        estimated_effort: `${highCount * 6} hours`
      }] : []),
      ...(mediumCount > 0 ? [{
        title: `Secure ${mediumCount} ${pluralize(mediumCount, 'Sector')}`,
        description: 'PRIORITY CHARLIE: Medium-level vulnerabilities in defensive positions. Schedule systematic sweep to reinforce weak points.',
        priority: 'medium',
        category: 'Perimeter Defense',
        action_items: vulnerabilityStats.grouped.medium.slice(0, 3).map(v => v.recommendation || `Secure: ${v.title || 'Defensive gap'}`),
        estimated_effort: `${mediumCount * 4} hours`
      }] : []),
      ...(lowCount > 0 ? [{
        title: `Patrol ${lowCount} ${pluralize(lowCount, 'Zone')}`,
        description: 'PRIORITY DELTA: Minor anomalies detected. Include in routine patrol schedule to maintain operational readiness.',
        priority: 'low',
        category: 'Routine Patrol',
        action_items: vulnerabilityStats.grouped.low.slice(0, 3).map(v => v.recommendation || `Monitor: ${v.title || 'Minor anomaly'}`),
        estimated_effort: `${lowCount * 2} hours`
      }] : []),
      ...(infoCount > 0 ? [{
        title: `Recon ${infoCount} ${pluralize(infoCount, 'Position')}`,
        description: 'INTEL REPORT: Reconnaissance data gathered. Analyze findings to fortify defenses and enhance situational awareness.',
        priority: 'info',
        category: 'Intelligence Gathering',
        action_items: vulnerabilityStats.grouped.info.slice(0, 3).map(v => v.recommendation || `Analyze: ${v.title || 'Intel item'}`),
        estimated_effort: `${Math.ceil(infoCount * 0.5)} hours`
      }] : [])
    ];

    console.log('Rendering remediation strategies:', strategies);

    // Use centralized metrics for display
    const totalEffort = effortHours;
    const severityBreakdown = [
      { name: 'Critical', count: criticalCount, color: '#FF6B6B', bgClass: 'coral-bg' },
      { name: 'High', count: highCount, color: '#FFD93D', bgClass: 'yellow-bg' },
      { name: 'Medium', count: mediumCount, color: '#E39FCE', bgClass: 'pink-bg' },
      { name: 'Low', count: lowCount, color: '#4CAF91', bgClass: 'green-bg' },
      { name: 'Info', count: infoCount, color: '#6DD4D9', bgClass: 'cyan-bg' }
    ].filter(s => s.count > 0);

    const maxSeverityCount = Math.max(...severityBreakdown.map(s => s.count), 1);
    // riskScore is now on a 0-10 scale to match normalizedRisk
    const riskColor = riskScore >= 8 ? '#FF6B6B' : riskScore >= 6 ? '#FFD93D' : riskScore >= 4 ? '#E39FCE' : '#4CAF91';

    return (
      <section className="results-section remediation-section">
        <div className="section-header-box cyan">
          <h2>Defense Playbook</h2>
          <div className="count-badge">{totalIssues}</div>
        </div>

        {(defensePlaybookSection.financial_impact || defensePlaybookSection.compliance) && (
          <div className="overview-grid scan-tab-brief-grid">
            <div className="overview-card green-bg">
              <div className="card-number">01</div>
              <h3 className="card-title">Potential Loss</h3>
              <p className="card-content">
                ₹{Number(defensePlaybookSection?.financial_impact?.potential_loss || potentialLoss || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="overview-card yellow-bg">
              <div className="card-number">02</div>
              <h3 className="card-title">Compliance</h3>
              <p className="card-content">
                {(defensePlaybookSection?.compliance?.frameworks || []).join(', ') || 'No frameworks configured'}
              </p>
            </div>
          </div>
        )}

        {/* Cost-Benefit Analysis - Full Width */}
        {hasVulnerabilities && (() => {
          const remediationCost = effectiveCostBenefit.total_remediation_cost || estimatedCost || 0;
          const breachCost = effectiveCostBenefit.potential_loss || potentialLoss || 0;
          const netSavings = effectiveCostBenefit.net_benefit || (breachCost - remediationCost) || 0;
          const roiPercent = Math.round(effectiveCostBenefit.roi_percentage || roiPercentage || 0);
          const maxCost = Math.max(remediationCost, breachCost);
          const remediationWidth = maxCost > 0 ? (remediationCost / maxCost) * 100 : 0;
          const breachWidth = maxCost > 0 ? (breachCost / maxCost) * 100 : 0;

          // Calculate risk level based on breach vs remediation ratio
          const riskRatio = remediationCost > 0 ? breachCost / remediationCost : 0;
          let riskLevel, riskColor, riskAction;
          if (riskRatio >= 10) {
            riskLevel = 'CRITICAL';
            riskColor = '#FF6B6B';
            riskAction = 'IMMEDIATE ACTION REQUIRED';
          } else if (riskRatio >= 5) {
            riskLevel = 'HIGH';
            riskColor = '#FFD93D';
            riskAction = 'PRIORITIZE REMEDIATION';
          } else if (riskRatio >= 2) {
            riskLevel = 'MODERATE';
            riskColor = '#E39FCE';
            riskAction = 'SCHEDULE REMEDIATION';
          } else {
            riskLevel = 'LOW';
            riskColor = '#4CAF91';
            riskAction = 'MONITOR AND MAINTAIN';
          }

          // Breakdown costs by category
          const breakdownItems = [
            { label: 'Critical Fixes', percent: criticalCount > 0 ? 40 : 0, color: '#FF6B6B' },
            { label: 'High Priority', percent: highCount > 0 ? 30 : 0, color: '#FFD93D' },
            { label: 'Medium Issues', percent: mediumCount > 0 ? 20 : 0, color: '#E39FCE' },
            { label: 'Low/Info Items', percent: (lowCount > 0 || infoCount > 0) ? 10 : 0, color: '#4CAF91' },
          ].filter(item => item.percent > 0);

          // Normalize percentages
          const totalPercent = breakdownItems.reduce((acc, item) => acc + item.percent, 0);
          breakdownItems.forEach(item => {
            item.percent = totalPercent > 0 ? Math.round((item.percent / totalPercent) * 100) : 0;
          });

          return (
            <div className="playbook-section cost-analysis-full">
              <div className="playbook-section-header green-bg">
                <h3>Cost-Benefit Analysis</h3>
                <span className="section-subtitle">Financial Impact Assessment</span>
              </div>
              <div className="playbook-section-content">
                {remediationCost === 0 && breachCost === 0 && (
                  <div className="cost-data-hint">
                    Cost model inputs are still warming up. Values will update as scanner confidence and asset context increase.
                  </div>
                )}
                {/* Top Stats Row */}
                <div className="cost-stats-row">
                  <div className="cost-stat-card">
                    <span className="cost-stat-label">Remediation Investment</span>
                    <span className="cost-stat-value">
                      {remediationCost > 0 ? `${`\u20B9`}${remediationCost.toLocaleString('en-IN')}` : 'Calculating...'}
                    </span>
                    <span className="cost-stat-hint">One-time security spend</span>
                  </div>
                  <div className="cost-stat-card danger">
                    <span className="cost-stat-label">Potential Breach Cost</span>
                    <span className="cost-stat-value">
                      {breachCost > 0 ? `${`\u20B9`}${breachCost.toLocaleString('en-IN')}` : 'Data pending'}
                    </span>
                    <span className="cost-stat-hint">If left unaddressed</span>
                  </div>
                  <div className="cost-stat-card success">
                    <span className="cost-stat-label">Net Savings</span>
                    <span className="cost-stat-value">
                      {netSavings > 0 ? `${`\u20B9`}${netSavings.toLocaleString('en-IN')}` : 'Pending model'}
                    </span>
                    <span className="cost-stat-hint">Risk avoided</span>
                  </div>
                  <div className="cost-stat-card roi">
                    <span className="cost-stat-label">Return on Investment</span>
                    <span className="cost-stat-value roi-value">{roiPercent > 0 ? `${roiPercent}%` : 'Pending'}</span>
                    <span className="cost-stat-hint">Value returned per rupee</span>
                  </div>
                </div>

                {/* Cost Comparison Visual */}
                <div
                  className="cost-comparison-section"
                  title="Cost estimates are derived from severity mix, CVSS intensity, and expected remediation effort."
                >
                  <h4 className="cost-section-title">Cost Comparison</h4>
                  <div className="cost-bars-container">
                    <div className="cost-bar-row">
                      <div className="cost-bar-label">
                        <span className="bar-name">Fix Now</span>
                        <span className="bar-value">{`\u20B9`}{remediationCost.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="cost-bar-track">
                        <div
                          className="cost-bar-fill remediation"
                          style={{ width: `${remediationWidth}%` }}
                        >
                          <span className="bar-percent">{remediationWidth > 10 ? `${Math.round(remediationWidth)}%` : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div className="cost-bar-row">
                      <div className="cost-bar-label">
                        <span className="bar-name">Risk Exposure</span>
                        <span className="bar-value">{`\u20B9`}{breachCost.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="cost-bar-track">
                        <div
                          className="cost-bar-fill breach"
                          style={{ width: `${breachWidth}%` }}
                        >
                          <span className="bar-percent">{breachWidth > 10 ? `${Math.round(breachWidth)}%` : ''}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="cost-multiplier">
                    <span className="multiplier-label">Risk Multiplier:</span>
                    <span className="multiplier-value">{riskRatio.toFixed(1)}x</span>
                    <span className="multiplier-desc">potential loss vs investment</span>
                  </div>
                </div>

                {/* Two Column: Breakdown + ROI Gauge */}
                <div className="cost-details-grid">
                  {/* Cost Breakdown by Category */}
                  <div className="cost-breakdown-card">
                    <h4 className="cost-section-title">Investment Breakdown</h4>
                    <div className="breakdown-chart">
                      <div className="breakdown-bars">
                        {breakdownItems.map((item, idx) => (
                          <div key={idx} className="breakdown-segment" style={{ flex: item.percent }}>
                            <div className="segment-fill" style={{ background: item.color }}></div>
                          </div>
                        ))}
                      </div>
                      <div className="breakdown-legend">
                        {breakdownItems.map((item, idx) => (
                          <div key={idx} className="legend-item">
                            <span className="legend-color" style={{ background: item.color }}></span>
                            <span className="legend-label">{item.label}</span>
                            <span className="legend-percent">{item.percent}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ROI Gauge */}
                  <div className="roi-gauge-card">
                    <h4 className="cost-section-title">ROI Assessment</h4>
                    <div className="roi-gauge">
                      <div className="gauge-visual">
                        <div className="gauge-track">
                          <div
                            className="gauge-fill"
                            style={{
                              width: `${Math.min(roiPercent / 10, 100)}%`,
                              background: roiPercent >= 500 ? '#4CAF91' : roiPercent >= 200 ? '#FFD93D' : '#E39FCE'
                            }}
                          ></div>
                        </div>
                        <div className="gauge-markers">
                          <span>0%</span>
                          <span>250%</span>
                          <span>500%</span>
                          <span>750%</span>
                          <span>1000%+</span>
                        </div>
                      </div>
                      <div className="gauge-result">
                        <span className="gauge-value">
                          {roiPercent}%
                        </span>
                        <span className="gauge-label">Return on Investment</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Assessment Banner */}
                <div className="risk-assessment-banner" style={{ borderColor: riskColor }}>
                  <div className="risk-badge" style={{ background: riskColor }}>
                    <span className="risk-level">{riskLevel}</span>
                    <span className="risk-label">RISK</span>
                  </div>
                  <div className="risk-content">
                    <span className="risk-action">{riskAction}</span>
                    <span className="risk-desc">
                      {riskRatio >= 10
                        ? `Potential breach cost is ${riskRatio.toFixed(0)}x your remediation investment. Every day of delay increases exposure.`
                        : riskRatio >= 5
                          ? `Breach costs ${riskRatio.toFixed(1)}x more than fixing. Strong case for immediate remediation.`
                          : riskRatio >= 2
                            ? `Moderate risk ratio of ${riskRatio.toFixed(1)}x. Plan remediation within your next sprint cycle.`
                            : `Risk ratio of ${riskRatio.toFixed(1)}x is manageable. Continue monitoring and maintain security posture.`
                      }
                    </span>
                  </div>
                  {effectiveCostBenefit.recommendation && (
                    <div className="risk-recommendation">
                      <strong>Recommendation:</strong> {effectiveCostBenefit.recommendation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Resource Allocation - Separate Section */}
        {hasVulnerabilities && effectiveResourceAllocation.team_composition && (
          <div className="playbook-section">
            <div className="playbook-section-header purple-bg">
              <h3>Resource Allocation</h3>
              <span className="section-subtitle">Team and Budget Planning</span>
            </div>
            <div className="playbook-section-content">
              <div className="resource-allocation-grid">
                <div className="resource-card">
                  <h4>Team Composition</h4>
                  <div className="team-members">
                    {Object.entries(effectiveResourceAllocation.team_composition).map(([role, count]) => (
                      <div key={role} className="team-member">
                        <span className="member-count">{count}</span>
                        <span className="member-role">{role}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {effectiveResourceAllocation.estimated_timeline && (
                  <div className="resource-card">
                    <h4>Estimated Timeline</h4>
                    <span className="resource-highlight">{effectiveResourceAllocation.estimated_timeline}</span>
                  </div>
                )}
                {effectiveResourceAllocation.budget_range && (
                  <div className="resource-card">
                    <h4>Budget Range</h4>
                    <span className="resource-highlight">{effectiveResourceAllocation.budget_range}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Items */}
        {effectiveRecommendations.length > 0 && (
          <div className="playbook-section">
            <div className="playbook-section-header coral-bg">
              <h3>Action Items</h3>
              <span className="section-count">{effectiveRecommendations.length}</span>
            </div>
            <div className="playbook-section-content no-padding">
              <div className="action-items-list">
                {effectiveRecommendations.map((rec, index) => (
                  <div key={index} className={`action-item ${rec.priority?.toLowerCase() || 'medium'}-priority`}>
                    <div className="action-item-marker">
                      <span className="marker-number">{index + 1}</span>
                    </div>
                    <div className="action-item-content">
                      <div className="action-item-header">
                        <span className={`priority-tag ${rec.priority?.toLowerCase() || 'medium'}`}>
                          {rec.priority?.toUpperCase() || 'MEDIUM'}
                        </span>
                        <h4>{rec.title || `Recommendation ${index + 1}`}</h4>
                        {rec.category && <span className="category-tag">{rec.category}</span>}
                      </div>
                      <p className="action-item-desc">{rec.description || rec.recommendation}</p>
                      {rec.action_items && rec.action_items.length > 0 && (
                        <div className="action-steps">
                          <strong>Steps:</strong>
                          <ol>
                            {rec.action_items.filter(a => a).map((action, idx) => (
                              <li key={idx}>{action}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {rec.estimated_effort && (
                        <div className="action-effort">
                          <strong>Effort:</strong> {rec.estimated_effort}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!hasVulnerabilities && (
          <div className="empty-state-card playbook-empty">
            <div className="empty-state-header">
              <div className="empty-state-box">TIMELINE</div>
            </div>
            <div className="empty-state-content">
              <h3>No Remediation Data</h3>
              <p>Timeline visualization will populate once the scan identifies actionable findings to prioritize.</p>
              <div className="empty-state-suggestions">
                <span className="suggestion-label">Next Steps:</span>
                <ul>
                  <li>Launch a new scan to generate vulnerability findings</li>
                  <li>Target may be fully patched - run Deep scan for thorough assessment</li>
                  <li>Check that target allows automated scanning without restrictions</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const getRiskColor = (score) => {
    if (!score) return '#999';
    if (score >= 8) return '#ff4444';
    if (score >= 6) return '#ff8800';
    if (score >= 4) return '#ffbb33';
    if (score >= 2) return '#00C851';
    return '#2db92d';
  };

  // Log rendering state
  console.log('Rendering ScanResults component');
  console.log('  - Active tab:', activeTab);
  console.log('  - Results loaded:', !!results);
  console.log('  - Results data:', results);

  // Helper to get scan mode display info
  const getScanModeInfo = () => {
    const mode = results?.scan_mode || 'standard';
    const scanTypes = results?.scan_types || [];

    const modeConfig = {
      quick: { 
        label: 'QUICK', 
        color: '#6DD4D9', 
        description: 'classification authorized. Executing a rapid, high-velocity surface sweep using Nuclei and Nikto to swiftly map immediate structural weaknesses and exposed ports.'
      },
      standard: { 
        label: 'STANDARD', 
        color: '#FFD93D', 
        description: 'classification authorized. Deploying a balanced matrix of standard security protocols including Nuclei, Wapiti, and Nikto for comprehensive baseline vulnerability assessment.'
      },
      deep: { 
        label: 'DEEP', 
        color: '#FF6B6B', 
        description: 'classification authorized. Orchestrating a synchronized, multi-vector deployment of advanced security engines including OWASP ZAP, Nuclei, Wapiti, and Nikto to map, enumerate, and extract potential surface vulnerabilities.'
      }
    };

    const config = modeConfig[mode] || modeConfig.standard;

    // If we have actual scan types, use them
    if (scanTypes.length > 0) {
      config.scanners = scanTypes.map(s => {
        if (s === 'owasp') return 'OWASP ZAP';
        if (s === 'nikto') return 'Nikto';
        if (s === 'nuclei') return 'Nuclei';
        if (s === 'wapiti') return 'Wapiti';
        return s.charAt(0).toUpperCase() + s.slice(1);
      }).join(' + ');
    }

    return config;
  };

  // Calculate scan duration
  const getScanDuration = () => {
    if (!results?.started_at) return null;

    const start = new Date(results.started_at);
    const end = results?.completed_at ? new Date(results.completed_at) : new Date();
    const durationMs = end - start;

    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      return `${hours}h ${remainingMins}m`;
    }

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    // Show milliseconds for durations less than 1 second
    return seconds > 0 ? `${seconds}s` : `${durationMs}ms`;
  };

  const scanModeInfo = getScanModeInfo();
  const scanDuration = getScanDuration();
  const displayProgress = Math.max(Number(results?.progress || 0), Number(liveProgress.progress || 0));
  const displayStage = liveProgress.phase || results?.current_stage || results?.status || 'unknown';
  const displayStatus = liveProgress.status || results?.status || 'unknown';
  const displayMessage = liveProgress.message || results?.scanner_debug?.status_message || results?.current_stage || '';
  const showLiveProgress = !['completed', 'failed', 'cancelled'].includes(String(displayStatus).toLowerCase());
  const startedAtLabel = results?.started_at ? new Date(results.started_at).toLocaleString() : 'N/A';
  const completedAtLabel = results?.completed_at ? new Date(results.completed_at).toLocaleString() : 'In progress';
  const lastEventTimestamp = results?.completed_at || results?.started_at;
  const lastEventAgeLabel = (() => {
    if (!lastEventTimestamp) {
      return 'Unknown';
    }
    const diffMs = Date.now() - new Date(lastEventTimestamp).getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  })();

  const handleGlobalFilterChange = (key, value) => {
    setGlobalFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const clearGlobalFilters = () => {
    setGlobalFilters({
      environment: 'all',
      assetType: 'all',
      exposure: 'all',
      query: ''
    });
  };

  return (
    <Layout>
      <div className="scan-results-container">
        <header className="results-header">
          <div className="header-content">
            <div className="results-header-title">
              <p className="scan-breadcrumb">Home &gt; Scans &gt; {scanId}</p>
              <h1>Scan Results</h1>
              <p className="scan-last-updated">Last update: {lastEventAgeLabel}</p>
            </div>
            <div className="header-actions">
              <button
                onClick={handleDownloadPDF}
                disabled={pdfGenerating}
                className="download-pdf-button"
                title="Export scan results as mission report"
              >
                {pdfGenerating ? 'Exporting Intel...' : 'Export Mission Report'}
              </button>
              <BackButton to="/" label="RETURN TO BASE" />
            </div>
          </div>
        </header>

        {/* Subtle Retro Decorator */}
        <div className="subtle-retro-bar" style={{ marginBottom: '1.5rem' }}>
          <div className="srb-left">
             <div className="srb-dot bg-blue"></div>
             <div className="srb-dot bg-green"></div>
             <div className="srb-dot bg-coral"></div>
          </div>
          <div className="srb-center" style={{ fontFamily: "var(--font-heading, 'VT323', monospace)", fontSize: '1.1rem', fontWeight: 'bold', letterSpacing: '0.1em' }}>
            SYS.LOG // {scanModeInfo?.label ? scanModeInfo.label.toUpperCase() : 'SCAN'} MODE
          </div>
          <div className="srb-right" style={{ display: 'flex', gap: '4px' }}>
             <div className="srb-block" style={{ width: '6px', height: '12px', background: '#ccc' }}></div>
             <div className="srb-block" style={{ width: '6px', height: '12px', background: '#aaa' }}></div>
             <div className="srb-block" style={{ width: '6px', height: '12px', background: '#666' }}></div>
          </div>
        </div>

        <div className="newspaper-spread np-scan-dossier-wrapper" style={{ paddingTop: '2.5rem', paddingBottom: 0, paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
            <div className="np-header-line" style={{ 
                borderTop: '5px solid var(--scan-text)', 
                borderBottom: '2px solid var(--scan-text)', 
                padding: '0.2rem 0',
                marginBottom: '0.6rem',
                fontFamily: "var(--font-heading, 'VT323', monospace)",
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <span>TARGET DOSSIER</span>
                <span>{results.target_url || 'Not available'}</span>
                <span>STATUS: {displayStatus}</span>
            </div>

                <div className="np-top-section" style={{ marginBottom: 0, alignItems: 'stretch' }}>
                    <div className="np-main-news" style={{ display: 'flex', flexDirection: 'column' }}>
                        <h1 className="np-huge-headline" style={{ margin: 0, fontSize: '2.4rem', lineHeight: '1' }}>THREAT INTEL</h1>
                        <h3 className="np-sub-headline" style={{ color: 'var(--scan-coral)', margin: '0.4rem 0 0.8rem 0', fontSize: '1.1rem' }}>TARGET ACQUIRED: {results.target_url}</h3>

                        {/* Metadata moved to Left Column */}
                        <div style={{ marginTop: '1rem', display: 'flex', gap: '2rem' }}>
                            <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 0.2rem 0', fontSize: '1rem', textTransform: 'uppercase' }}>SCAN ID</h4>
                                <p style={{ margin: 0 }}><code style={{ background: 'transparent', padding: 0, fontFamily: "'Courier New', Courier, monospace", fontSize: '0.9rem', wordBreak: 'break-all', opacity: 0.8 }}>{scanId}</code></p>
                            </div>
                            
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '1rem', textTransform: 'uppercase', textAlign: 'center' }}>THREAT SIGNALS</h4>
                                <p style={{ margin: 0, textAlign: 'center' }}>
                                  <code style={{ background: 'var(--scan-yellow)', padding: '0.3rem 0.8rem', border: '2px solid var(--scan-text)', color: 'var(--scan-text)', fontWeight: 'bold', fontFamily: "var(--font-heading, 'VT323', monospace)", fontSize: '1.2rem', boxShadow: '2px 2px 0 var(--scan-text)' }}>
                                    {vulnerabilityStats.total} DETECTED
                                  </code>
                                </p>
                            </div>
                            
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontFamily: "var(--font-heading, 'VT323', monospace)", fontSize: '1.1rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                        <span style={{ color: 'var(--scan-cyan)' }}>SCAN MODE // </span>
                                        <span style={{ color: 'var(--scan-text)' }}>{scanModeInfo.label}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                        <span style={{ color: 'var(--scan-cyan)' }}>DURATION // </span>
                                        <span style={{ color: 'var(--scan-text)' }}>{scanDuration || 'PENDING'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* KPI Metrics */}
                        {missionBriefKpis.length > 0 && (
                            <div style={{ borderTop: '2px dashed var(--scan-text)', paddingTop: '0.8rem', marginTop: '1.2rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                                    {missionBriefKpis.map((metric, index) => {
                                        let align = 'center';
                                        if (index === 0) align = 'left';
                                        if (index === 3) align = 'right';
                                        
                                        return (
                                            <div key={metric.label} style={{ textAlign: align }}>
                                                <div style={{ fontFamily: "var(--font-heading, 'VT323', monospace)", fontSize: '1.8rem', fontWeight: 'bold', lineHeight: '1', color: 'var(--scan-coral)' }}>{metric.value}</div>
                                                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.8, marginTop: '0.3rem' }}>{metric.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="np-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <div className="np-section-header" style={{ marginBottom: '0.5rem', padding: '0.3rem 0.6rem', fontSize: '1.1rem' }}>MISSION BRIEF</div>
                        
                        {missionBriefBullets.length > 0 && (
                            <ul style={{ listStyleType: 'none', padding: 0, margin: '0 0 1rem 0' }}>
                                {missionBriefBullets.map((bullet, index) => (
                                    <li key={index} style={{ marginBottom: '0.8rem', fontSize: '1rem', lineHeight: '1.4', position: 'relative', paddingLeft: '1.2rem' }}>
                                        <span style={{ position: 'absolute', left: 0, top: '4px', fontSize: '0.7em', color: 'var(--scan-coral)' }}>■</span>
                                        {bullet}
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="np-sidebar-widget" style={{ marginTop: 'auto' }}></div>

                    </div>
                </div>
            </div>
        <div className="scan-results-workspace">
          {showLiveProgress && (
            <section className="scan-live-progress" aria-live="polite">
              <div className="scan-live-progress__head">
                <span className="scan-live-progress__stage">{displayStage}</span>
                <span className="scan-live-progress__percent">{displayProgress}%</span>
              </div>
              <div className="scan-live-progress__bar" role="progressbar" aria-valuenow={displayProgress} aria-valuemin="0" aria-valuemax="100">
                <span style={{ width: `${displayProgress}%` }} />
              </div>
              {displayMessage && (
                <ExpandableContent maxHeight={72} showMoreText="Show full update" showLessText="Show less">
                  <p>{displayMessage}</p>
                </ExpandableContent>
              )}
            </section>
          )}


          <div className="sticky-command-center">


            {availableTabs.length > 0 && (
              <>
                <div className="scan-tabs-divider" aria-hidden="true"></div>
                <div className="tabs-shell">
                  <TabbedInterface
                    tabs={availableTabs.map((tab) => {
                      const tabColors = {
                        summary: 'tab tab-yellow',
                        overview: 'tab tab-pink',
                        vulnerabilities: 'tab tab-coral',
                        mitre: 'tab tab-green',
                        remediation: 'tab tab-cyan',
                        ai: 'tab tab-pink'
                      };
                      return {
                        id: tab.id,
                        label: tab.display,
                        className: `${tabColors[tab.id] || 'tab'} ${activeTab === tab.id ? 'active' : ''}`,
                      };
                    })}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    renderPanel={false}
                    className="tabs-container"
                  />
                </div>
              </>
            )}
          </div>

          <div className="tabs-content">
            {activeTab === 'overview' && renderRiskSection()}
            {activeTab === 'summary' && renderExecutiveSummary()}
            {activeTab === 'vulnerabilities' && renderVulnerabilitySection()}
            {activeTab === 'mitre' && renderMITRESection()}
            {activeTab === 'remediation' && renderRemediationStrategies()}
            {activeTab === 'ai' && renderAIAnalysis()}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ScanResults;
